// src/components/Interfaz.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  Dog,
  Cat,
  ArrowUpRight,
  Pencil,
  Lock,
  Trash2,
  X,
  Eye,
  EyeOff,
  PlayCircle,
  Bell,
  Menu,
  Cake,
  Stethoscope,
  Clock,
  Sparkles,
  Phone,
  CalendarHeart,
  Trophy,
  Check,
  Bookmark,
  Play,
  PawPrint,
  Flame,
} from "lucide-react";
import "../styles/interfaz.css";
import PetfamCalendar from "./PetfamCalendar";
import { getBreedImage } from "../config/petBreedImages";
import VetAssistantChat from "./VetAssistantChat";
import petfamLogo from "../assets/Petfamp.png";
import config from "../config";
import { authFetch } from "../utils/authFetch";

import chipImg from "../assets/chip.webp";
import visa from "../assets/visa.webp";
import mc from "../assets/mastercard.webp";
import amex from "../assets/amex.webp";

const API_BASE_URL = String(config.apiBaseUrl || "").replace(/\/+$/, "");

const IS_QA =
  (window?.location?.hostname || "").toLowerCase().includes("qa") ||
  (API_BASE_URL || "").toLowerCase().includes("qa");

// ✅ MODO PRUEBAS: NUNCA se bloquea la interfaz por plan
const PLAN_ACTIVE_FOR_TESTS = true;

// 💚 Recordatorios de salud
const HEALTH_REMINDERS = [
  "Tu mascota no puede decirte cuándo algo no está bien. La prevención permite detectar riesgos antes de que aparezcan los síntomas.",
  "El comportamiento es una forma de comunicación. Cambios en su conducta siempre tienen una causa. Aprende a interpretarlos con nuestro acompañamiento de veterinario conductal.",
  "La salud no se cuida solo cuando hay señales visibles. El seguimiento constante marca la diferencia. Descubre por qué la prevención importa.",
  "La ansiedad y el estrés no son mañas. Son respuestas emocionales que necesitan manejo adecuado. Agenda tu consulta con veterinario conductual.",
  "Pequeños cambios hoy pueden evitar grandes problemas mañana. Detectarlos a tiempo es clave. Activa tu seguimiento preventivo.",
  "Castigar no corrige el comportamiento, lo oculta. Entender la causa permite soluciones reales. Conoce un enfoque basado en ciencia.",
  "El bienestar emocional y el físico están conectados. Ignorar uno afecta el otro. Cuida ambos con PETFAM.",
  "Cada etapa de la vida requiere cuidados distintos. La edad también influye en la conducta. Acompaña cada etapa con expertos.",
  "Muchos problemas de comportamiento se construyen en silencio. La intervención temprana mejora la convivencia. Consulta con nuestro equipo especializado.",
  "Un control a tiempo puede evitar tratamientos largos y costosos. La prevención también es tranquilidad.",
  "El entorno influye más de lo que imaginas. Rutinas, espacio y cambios impactan el comportamiento. Aprende a crear un ambiente saludable.",
  "Cuidar a tu mascota es un proceso continuo, no un evento aislado. El acompañamiento hace la diferencia. Agenda una consulta con nuestros especialistas.",
];

// 🩺 Placeholders visuales para la tarjeta de mascota (pendiente de conectar
// a un endpoint real de estado de ánimo). Se calculan de forma
// determinística a partir del id de la mascota para que no "salten" en cada
// render, pero NO representan datos médicos reales todavía. La barra de
// SALUD ya NO usa este placeholder salvo como último respaldo — ver
// computePetCarePercent más abajo.
const PET_MOOD_PLACEHOLDERS = [
  "Con energía de sobra",
  "Modo siesta activado",
  "Listo para el parque",
  "Explorando el día",
  "Feliz y juguetón",
  "Tranquilo y consentido",
];

function hashStringToInt(str) {
  let hash = 0;
  const s = String(str ?? "");
  for (let i = 0; i < s.length; i++) {
    hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function getPetMood(pet) {
  const idx = hashStringToInt(pet.id) % PET_MOOD_PLACEHOLDERS.length;
  return PET_MOOD_PLACEHOLDERS[idx];
}

function getPetHealthPercent(pet) {
  const idx = hashStringToInt(pet.id);
  return 75 + (idx % 21); // valor pseudo-estable entre 75% y 95%
}

const MONTH_ABBR = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

function getPetNextEvent(pet) {
  const parsed = parseLocalDateParts(pet.nacimiento);
  if (parsed) {
    return `Cumpleaños · ${parsed.day} ${MONTH_ABBR[parsed.month]}`;
  }
  return "Sin eventos próximos";
}

// =========================================================
// ✅ RITUALES DE VÍNCULO DIARIO — "Cuidados de hoy"
// El banco de tareas y el algoritmo de selección ahora viven en el backend
// (DailyCareTaskBank.java / DailyCareService.java), que es la fuente de
// verdad. Aquí solo queda lo que el frontend necesita para MOSTRAR lo que
// el backend ya calculó: las etiquetas legibles de cada categoría y el
// copy de la meta del mes según el %.
// =========================================================

const CATEGORY_LABELS = {
  movimiento: "Movimiento",
  higiene: "Higiene",
  nutricion: "Nutrición",
  enriquecimiento: "Enriquecimiento",
  vinculo: "Vínculo",
  salud: "Salud",
  aprendizaje: "Aprendizaje",
};

function getDayNumber() {
  return Math.floor(Date.now() / (1000 * 60 * 60 * 24));
}

function getGoalCopy(percent) {
  if (percent >= 100) {
    return { title: "¡Mes perfecto!", desc: "Completaste tus cuidados todos los días de este mes." };
  }
  if (percent >= 70) {
    return { title: "Vas muy bien", desc: "Sigue así para cerrar un mes perfecto." };
  }
  if (percent >= 40) {
    return { title: "Vas por buen camino", desc: "Cada día que los cuidas suma para tu meta." };
  }
  return { title: "Empecemos con toda", desc: "Marca los cuidados de hoy para subir tu racha." };
}

// 📚 Contenido de interés: guías cortas y videos (catálogo fijo del
// frontend, pendiente de conectar a un CMS real). El número de guardados
// que se muestra en cada tarjeta YA NO es un dato fijo — viene del backend
// (petfam_content_save) y refleja los guardados reales de todos los
// usuarios de la plataforma.
const CONTENT_CATEGORIES = ["Todo", "Salud", "Nutrición", "Comportamiento"];

const CONTENT_ARTICLES = [
  {
    id: "cepillado",
    category: "Salud",
    categorySlug: "salud",
    title: "Cepillado en casa: la rutina de 6 minutos",
    description: "Cómo convertir el cepillado en un ritual que tu perro pida, y qué dice su pelaje sobre su salud.",
    readTime: 6,
  },
  {
    id: "porciones",
    category: "Nutrición",
    categorySlug: "nutricion",
    title: "Porciones reales según peso y edad",
    description: "La tabla que usan los veterinarios para calcular calorías, más cinco ingredientes frescos seguros.",
    readTime: 8,
  },
  {
    id: "enriquecimiento",
    category: "Comportamiento",
    categorySlug: "comportamiento",
    title: "Enriquecimiento mental para gatos de interior",
    description: "Diez minutos de juego dirigido cansan más que una hora sueltos. Te mostramos el circuito exacto.",
    readTime: 5,
  },
];

// 🎗️ Frases del ticker deslizante del pie de página (contenido fijo de marca)
const TICKER_ITEMS = [
  "Recordatorios inteligentes",
  "Veterinarios verificados",
  "Racha de cuidado",
  "Contenido con expertos",
  "Vacunas al día",
];

function normalizeSpecies(v) {
  const s = String(v || "").trim().toLowerCase();

  if (s === "perro" || s === "dog" || s === "dogs" || s === "canino" || s === "canina") return "Perro";
  if (s === "gato" || s === "cat" || s === "cats" || s === "felino" || s === "felina") return "Gato";

  if (s === "perros") return "Perro";
  if (s === "gatos") return "Gato";

  return v || "";
}

function toTitleCaseName(name) {
  const s = String(name || "").trim();
  if (!s) return "";

  return s
    .toLowerCase()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function parseLocalDateParts(dateString) {
  if (!dateString) return null;

  const base = String(dateString).trim().slice(0, 10);
  const [year, month, day] = base.split("-").map(Number);

  if (!year || !month || !day) return null;

  return {
    year,
    month: month - 1,
    day,
  };
}

// 🐾 Formatea la lista de nombres de mascotas del pie del panel de
// notificaciones ("PETFAM CUIDA A X, Y Y Z") a partir de los datos reales
// de `pets`. Sin mascotas cae en un texto genérico en vez de romper el layout.
function formatPetNamesLabel(pets) {
  const names = (pets || [])
    .map((p) => String(p?.nombre || "").trim())
    .filter(Boolean)
    .map((n) => n.toUpperCase());

  if (names.length === 0) return "TUS MASCOTAS";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} Y ${names[1]}`;

  return `${names.slice(0, -1).join(", ")} Y ${names[names.length - 1]}`;
}

// 🎂 Notificaciones de cumpleaños: helpers para que "✓ Listo" quede
// marcado como leída todo el día de hoy (persistido en localStorage),
// y que al cambiar el día la clave cambie sola — sin necesidad de limpiar
// nada, esa mascota deja de coincidir con "hoy es su cumpleaños" y la
// notificación simplemente no se vuelve a generar.
function getTodayDateKey() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// 🩺 Devuelve la fecha (YYYY-MM-DD) de hoy +/- N días, para comparar contra
// las fechas de eventos del calendario (vacunas/desparasitación) y detectar
// si caen en "2 días antes", "mismo día" o "1 día después" — la misma regla
// que usa el backend para disparar los correos (VaccineReminderScheduler).
function getDateKeyOffset(daysOffset) {
  const d = new Date();
  d.setDate(d.getDate() + daysOffset);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Genérico: sirve tanto para cumpleaños como para vacunas/desparasitaciones —
// cualquier notificación real (no demo) marcada "✓ Listo" queda leída todo el
// día de hoy, y al cambiar de día la clave cambia sola.
function getTodayReadStorageKey(uid) {
  return `petfam_notifs_read_${uid ?? "anon"}_${getTodayDateKey()}`;
}

function getTodayReadIds(uid) {
  try {
    const raw = localStorage.getItem(getTodayReadStorageKey(uid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function addTodayReadId(uid, id) {
  try {
    const key = getTodayReadStorageKey(uid);
    const current = getTodayReadIds(uid);
    if (!current.includes(id)) {
      localStorage.setItem(key, JSON.stringify([...current, id]));
    }
  } catch {}
}

// Igual que lo anterior, pero para notificaciones BORRADAS: si el usuario
// le da a la "✕", esa notificación (cumpleaños o salud) no debe reaparecer
// hoy, aunque el useEffect que la genera se vuelva a ejecutar. Al día
// siguiente la clave cambia sola y puede volver a aparecer si aplica.
function getTodayDismissedStorageKey(uid) {
  return `petfam_notifs_dismissed_${uid ?? "anon"}_${getTodayDateKey()}`;
}

function getTodayDismissedIds(uid) {
  try {
    const raw = localStorage.getItem(getTodayDismissedStorageKey(uid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function addTodayDismissedId(uid, id) {
  try {
    const key = getTodayDismissedStorageKey(uid);
    const current = getTodayDismissedIds(uid);
    if (!current.includes(id)) {
      localStorage.setItem(key, JSON.stringify([...current, id]));
    }
  } catch {}
}

// ⏱️ ORDEN de la campanita: cada notificación (por su id) guarda la
// primera vez que se vio, sin importar si luego se marca "✓ Listo" o no
// — así una notificación NUEVA siempre aparece de primeras, y una ya
// vista mantiene su puesto aunque siga sin leer. Leída/no leída ya NO
// decide el orden, solo cuándo apareció por primera vez.
function getNotifFirstSeenStorageKey(uid) {
  return `petfam_notifs_first_seen_${uid ?? "anon"}`;
}

function getNotifFirstSeenMap(uid) {
  try {
    const raw = localStorage.getItem(getNotifFirstSeenStorageKey(uid));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function setNotifFirstSeenMap(uid, map) {
  try {
    localStorage.setItem(getNotifFirstSeenStorageKey(uid), JSON.stringify(map));
  } catch {}
}


// 📋 Notificaciones de resumen de tareas de las 8pm: cada día genera la
// suya (con su propio id fechado), y a diferencia de las demás (que se
// limpian solas cada medianoche) cada una queda viva hasta que el usuario
// le dé su propia "✕" — por eso pueden acumularse varios días seguidos si
// no se borran. Se guardan en una LISTA, en una clave SIN la fecha de hoy,
// con el contenido de cada una ya congelado en el momento en que se generó.
function getEveningTaskNotifsStorageKey(uid) {
  return `petfam_task_evening_notifs_${uid ?? "anon"}`;
}

function getStoredEveningTaskNotifs(uid) {
  try {
    const raw = localStorage.getItem(getEveningTaskNotifsStorageKey(uid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((n) => n && n.id) : [];
  } catch {
    return [];
  }
}

function setStoredEveningTaskNotifs(uid, list) {
  try {
    localStorage.setItem(getEveningTaskNotifsStorageKey(uid), JSON.stringify(list));
  } catch {}
}

function addStoredEveningTaskNotif(uid, notif) {
  const current = getStoredEveningTaskNotifs(uid);
  if (current.some((n) => n.id === notif.id)) return current;
  const updated = [...current, notif];
  setStoredEveningTaskNotifs(uid, updated);
  return updated;
}

function updateStoredEveningTaskNotifRead(uid, id) {
  const current = getStoredEveningTaskNotifs(uid);
  const updated = current.map((n) => (n.id === id ? { ...n, read: true } : n));
  setStoredEveningTaskNotifs(uid, updated);
}

function removeStoredEveningTaskNotif(uid, id) {
  const current = getStoredEveningTaskNotifs(uid);
  const updated = current.filter((n) => n.id !== id);
  setStoredEveningTaskNotifs(uid, updated);
}

// 🩺 Mismo patrón de persistencia que las de cumpleaños, pero para las
// notificaciones de vacunas/desparasitación: "✓ Listo" queda marcado todo
// el día de hoy, y al día siguiente la clave cambia sola.
function getHealthReadStorageKey(uid) {
  return `petfam_health_notifs_read_${uid ?? "anon"}_${getTodayDateKey()}`;
}

function getReadHealthIds(uid) {
  try {
    const raw = localStorage.getItem(getHealthReadStorageKey(uid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function addReadHealthId(uid, id) {
  try {
    const key = getHealthReadStorageKey(uid);
    const current = getReadHealthIds(uid);
    if (!current.includes(id)) {
      localStorage.setItem(key, JSON.stringify([...current, id]));
    }
  } catch {}
}

// =========================================================
// 📚 NOTIFICACIÓN "NUEVO CONTENIDO SUBIDO" — PLANTILLA MANUAL
// Cada vez que subas contenido nuevo, edita SOLO estos 4 campos y listo:
// - title / text: el copy que se muestra en la campanita.
// - targetCategory: a qué categoría de "Contenido de interés" debe llevar
//   el botón "Leer ahora" (una de: "Todo", "Salud", "Nutrición",
//   "Comportamiento").
// - id: cámbialo cada vez que edites el contenido — eso hace que la
//   notificación vuelva a aparecer para TODOS los usuarios, incluso a
//   quien ya había borrado la anterior. Si dejas el mismo id, a quien ya
//   la vio/borró no le vuelve a salir.
// Para no mostrar ninguna, deja NEW_CONTENT_NOTIFICATION en null.
// =========================================================
const NEW_CONTENT_NOTIFICATION = {
  id: "new-content-porciones-peso-edad",
  title: "Nuevo para ti: porciones según peso y edad",
  text: "La tabla que usan los veterinarios para calcular calorías, más cinco ingredientes frescos seguros. Lectura de 8 minutos.",
  targetCategory: "Nutrición",
};

// A diferencia de las demás notificaciones (que se limpian cada
// medianoche), esta debe poder quedarse varios días sin leer/sin borrar
// — así que su estado de lectura/borrado NO usa la clave de "hoy", sino
// una permanente por usuario.
function getContentNotifPermanentKey(kind, uid) {
  return `petfam_content_notif_${kind}_${uid ?? "anon"}`;
}

function getPermanentContentIds(kind, uid) {
  try {
    const raw = localStorage.getItem(getContentNotifPermanentKey(kind, uid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function addPermanentContentId(kind, uid, id) {
  try {
    const key = getContentNotifPermanentKey(kind, uid);
    const current = getPermanentContentIds(kind, uid);
    if (!current.includes(id)) {
      localStorage.setItem(key, JSON.stringify([...current, id]));
    }
  } catch {}
}

// =========================================================
// 🔋 NOTIFICACIÓN DE "ENERGÍA" — cambios reales en el % de cuidados
// Usa el mismo dato real que la barra de SALUD de cada tarjeta de mascota
// (petCarePercents, del endpoint /pets/{id}/care-percent). Se guarda un
// valor base por mascota para detectar cuándo de verdad cambió (subió o
// bajó) y no repetir aviso en cada render. Igual que "nuevo contenido",
// no se limpia sola a medianoche: cada cambio detectado queda como su
// propia notificación permanente hasta que el usuario le dé "✕".
// =========================================================
function getLastKnownCarePercentsKey(uid) {
  return `petfam_last_care_percents_${uid ?? "anon"}`;
}

function getLastKnownCarePercents(uid) {
  try {
    const raw = localStorage.getItem(getLastKnownCarePercentsKey(uid));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function setLastKnownCarePercents(uid, obj) {
  try {
    localStorage.setItem(getLastKnownCarePercentsKey(uid), JSON.stringify(obj));
  } catch {}
}

function getEnergyNotifsStorageKey(uid) {
  return `petfam_energy_notifs_${uid ?? "anon"}`;
}

function getStoredEnergyNotifs(uid) {
  try {
    const raw = localStorage.getItem(getEnergyNotifsStorageKey(uid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((n) => n && n.id) : [];
  } catch {
    return [];
  }
}

function setStoredEnergyNotifs(uid, list) {
  try {
    localStorage.setItem(getEnergyNotifsStorageKey(uid), JSON.stringify(list));
  } catch {}
}

function addStoredEnergyNotif(uid, notif) {
  const current = getStoredEnergyNotifs(uid);
  if (current.some((n) => n.id === notif.id)) return current;
  const updated = [...current, notif];
  setStoredEnergyNotifs(uid, updated);
  return updated;
}

function updateStoredEnergyNotifRead(uid, id) {
  const current = getStoredEnergyNotifs(uid);
  const updated = current.map((n) => (n.id === id ? { ...n, read: true } : n));
  setStoredEnergyNotifs(uid, updated);
}

function removeStoredEnergyNotif(uid, id) {
  const current = getStoredEnergyNotifs(uid);
  const updated = current.filter((n) => n.id !== id);
  setStoredEnergyNotifs(uid, updated);
}


export default function Interfaz() {

  // ✅ Chat Dra. Vetia
  const [openVetChat, setOpenVetChat] = useState(false);
  const [chatPet, setChatPet] = useState(null);

  const [openDrPawsPicker, setOpenDrPawsPicker] = useState(false);
  const [drPawsMode, setDrPawsMode] = useState("CHAT"); // "CHAT" | "CONSULTA"

  const navigate = useNavigate();
  const location = useLocation();

  // 🔹 sección activa del header (Inicio / Tus mascotas / Calendario / Contenido)
  const [activeNav, setActiveNav] = useState("inicio");
  const topRef = useRef(null);
  const reminderRef = useRef(null);
  const heroRef = useRef(null);
  const calendarRef = useRef(null);
  const dailyCareRef = useRef(null);

  // ===== Header: user menu =====
  const [openMenu, setOpenMenu] = useState(false);
  const [openVideoGuide, setOpenVideoGuide] = useState(false);
  const [isMobileVideo, setIsMobileVideo] = useState(false);
  const menuRef = useRef(null);
  const toggleMenu = () => setOpenMenu((v) => !v);

  // ===== Header: menú móvil (hamburguesa) =====
  const [openMobileNav, setOpenMobileNav] = useState(false);
  const mobileNavRef = useRef(null);

  useEffect(() => {
  const checkDevice = () => {
    setIsMobileVideo(window.innerWidth <= 768);
  };

  checkDevice();
  window.addEventListener("resize", checkDevice);

  return () => window.removeEventListener("resize", checkDevice);
}, []);

useEffect(() => {
  const raw = localStorage.getItem("petfamUser");
  if (!raw) return;

  try {
    const user = JSON.parse(raw);
    const id = user?.idUser ?? user?.id ?? user?.userId;
    if (!id) return;

    const videoGuideSeenKey = `petfam_video_guide_seen_${id}`;
    const alreadySeen = localStorage.getItem(videoGuideSeenKey);

    if (!alreadySeen) {
      const timer = setTimeout(() => {
        setOpenVideoGuide(true);
        localStorage.setItem(videoGuideSeenKey, "true");
      }, 500);

      return () => clearTimeout(timer);
    }
  } catch {}
}, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setOpenNotifications(false);
      if (mobileNavRef.current && !mobileNavRef.current.contains(e.target)) setOpenMobileNav(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const onEsc = (e) => {
      if (e.key !== "Escape") return;
      setOpenMenu(false);
      setOpenNotifications(false);
      setOpenMobileNav(false);
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      // Header nav: resalta la sección más cercana a la parte superior visible
      const navTargets = [
        { key: "inicio", el: heroRef.current },
        { key: "mascotas", el: topRef.current },
        { key: "calendario", el: calendarRef.current },
        { key: "contenido", el: reminderRef.current },
      ];

      let closestKey = "inicio";
      let closestDist = Infinity;
      navTargets.forEach(({ key, el }) => {
        if (!el) return;
        const dist = Math.abs(el.getBoundingClientRect().top - 130);
        if (dist < closestDist) {
          closestDist = dist;
          closestKey = key;
        }
      });
      setActiveNav(closestKey);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ===== PERFIL DE USUARIO =====
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profileName, setProfileName] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [profileLocation, setProfileLocation] = useState("");
  const [openProfilePanel, setOpenProfilePanel] = useState(false);
  const profileFileRef = useRef(null);
  const [userId, setUserId] = useState(null);

  const [country, setCountry] = useState("");

  // ===== ACTUALIZAR INFO DE PAGO =====
const [openPaymentModal, setOpenPaymentModal] = useState(false);
const [payLoading, setPayLoading] = useState(false);
const [payError, setPayError] = useState("");
const [paySuccess, setPaySuccess] = useState(false);

const [cardType, setCardType] = useState("visa"); // "visa" | "mc" | "amex"
const [cardHolder, setCardHolder] = useState("");
const [cardNumber, setCardNumber] = useState("");
const [cardExpMonth, setCardExpMonth] = useState("");
const [cardExpYear, setCardExpYear] = useState("");
const [cardCvc, setCardCvc] = useState("");

  // ===== CAMBIO DE CONTRASEÑA =====
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
const [showNewPassword, setShowNewPassword] = useState(false);
const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ===== NOTIFICACIONES (header) =====
  const [openNotifications, setOpenNotifications] = useState(false);
  const notifRef = useRef(null);

  // 🔔 Notificaciones del header. Las de cumpleaños, vacunas/desparasitación
  // y tareas del día ya NO son placeholders: se calculan más abajo con
  // sendos useEffect a partir de datos reales (`pets`, `/calendar/events` y
  // `dailyCareTasks`).
  const [notifications, setNotifications] = useState([]);

  // ===== MASCOTAS =====
  const [pets, setPets] = useState([]);
  const [filter, setFilter] = useState("todas");

  // ✅ Id de la mascota cuya explicación de "CUIDADOS" está abierta (tarjetas)
  const [openHealthInfoPetId, setOpenHealthInfoPetId] = useState(null);
  // Posición (viewport) del botón "?" que se tocó, para pintar el globo
  // fuera de la tarjeta vía portal — la tarjeta tiene overflow:hidden
  // (para recortar la foto en las esquinas), así que el globo NUNCA
  // podría verse completo si se quedara dentro de ese contenedor.
  const [healthInfoAnchorRect, setHealthInfoAnchorRect] = useState(null);
  // Botón "?" real de cada tarjeta, por id de mascota — para poder volver a
  // medir su posición cada vez que la página hace scroll (el globo vive en
  // un portal, así que no se mueve solo con el layout).
  const healthInfoBtnRefs = useRef({});

  // Mientras el globo esté abierto, recalcula su posición en cada scroll
  // (el scroll real ocurre dentro de .page-enter, no en la ventana — por
  // eso el listener va en fase de captura, que sí detecta scroll de
  // contenedores internos) y en cada resize. Así el globo sigue al botón
  // en vez de quedarse "pegado" donde se abrió.
  useEffect(() => {
    if (!openHealthInfoPetId) return;

    const updatePosition = () => {
      const el = healthInfoBtnRefs.current[openHealthInfoPetId];
      if (el) setHealthInfoAnchorRect(el.getBoundingClientRect());
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [openHealthInfoPetId]);

  // ===== ✅ Cuidados de hoy (ahora vienen del backend) =====
  const [dailyCareTasks, setDailyCareTasks] = useState([]);
  const [streakCount, setStreakCount] = useState(0);
  const [dailyCareLoading, setDailyCareLoading] = useState(false);

  // ===== ✅ % de "salud" (cuidados del mes) por mascota, desde el backend =====
  const [petCarePercents, setPetCarePercents] = useState({});

  const mapDailyCareTaskDTO = (t) => ({
    id: t.id,
    taskDefId: t.taskDefId,
    title: t.title,
    category: t.category,
    points: t.points,
    done: !!t.done,
    idPet: t.idPet,
    petName: t.petName,
  });

  const applyDailyCarePayload = (payload) => {
    if (!payload) return;
    setDailyCareTasks((payload.tasks || []).map(mapDailyCareTaskDTO));
    setStreakCount(payload.streakCount || 0);
    setMonthlyGoalPercent(payload.monthlyGoalPercent || 0);
  };

  // Trae las tareas de "Cuidados de hoy" del backend (las genera solo si es
  // la primera vez que se piden ese día). Se usa al montar/cambiar de
  // usuario, y como respaldo si un toggle falla.
  const fetchDailyCare = async (uid) => {
    if (!uid) {
      setDailyCareTasks([]);
      setStreakCount(0);
      setMonthlyGoalPercent(0);
      return;
    }

    try {
      setDailyCareLoading(true);
      const res = await authFetch(`/daily-care/today?idUser=${uid}`);
      const json = await res.json().catch(() => null);

      if (res.ok && json?.data) {
        applyDailyCarePayload(json.data);
      } else {
        setDailyCareTasks([]);
      }
    } catch (err) {
      console.error("Error cargando cuidados de hoy", err);
      setDailyCareTasks([]);
    } finally {
      setDailyCareLoading(false);
    }
  };

  useEffect(() => {
    fetchDailyCare(userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Trae el % de "SALUD" (cuidados del mes) de cada mascota real, para la
  // barra de cada tarjeta. Se limita a 2 mascotas, igual que el backend.
  const loadPetCarePercents = async (uid, petsList) => {
    if (!uid || !petsList || petsList.length === 0) {
      setPetCarePercents({});
      return;
    }

    const entries = await Promise.all(
      petsList.slice(0, 2).map(async (pet) => {
        try {
          const res = await authFetch(`/pets/${pet.id}/care-percent?idUser=${uid}`);
          const json = await res.json().catch(() => null);
          if (res.ok && typeof json?.data === "number") {
            return [String(pet.id), json.data];
          }
        } catch {}
        return [String(pet.id), null];
      })
    );

    setPetCarePercents(Object.fromEntries(entries));
  };

  useEffect(() => {
    loadPetCarePercents(userId, pets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pets, userId]);

  // Marca/desmarca una tarea en el backend. Actualización optimista para
  // que se sienta instantáneo, con reversión (recarga real) si algo falla.
  const toggleDailyCareTask = async (idDailyCareTask) => {
    if (!userId || !idDailyCareTask) return;

    setDailyCareTasks((prev) =>
      prev.map((t) => (t.id === idDailyCareTask ? { ...t, done: !t.done } : t))
    );

    try {
      const res = await authFetch(`/daily-care/tasks/${idDailyCareTask}/toggle?idUser=${userId}`, {
        method: "PUT",
      });
      const json = await res.json().catch(() => null);

      if (res.ok && json?.data) {
        applyDailyCarePayload(json.data);
      } else {
        await fetchDailyCare(userId);
      }
    } catch (err) {
      console.error("Error actualizando tarea de cuidados", err);
      await fetchDailyCare(userId);
    }

    // La barra de SALUD de la mascota afectada también pudo cambiar
    loadPetCarePercents(userId, pets);
  };

  const dailyCareDoneCount = dailyCareTasks.filter((t) => t.done).length;
  const dailyCareTotal = dailyCareTasks.length;
  const dailyCareProgressPercent = dailyCareTotal > 0 ? Math.round((dailyCareDoneCount / dailyCareTotal) * 100) : 0;
  const dailyCarePoints = dailyCareTasks.reduce((sum, t) => sum + (t.done ? t.points : 0), 0);

  // ===== ✅ Contenido guardado (backend: petfam_content_save) =====
  const [savedContentIds, setSavedContentIds] = useState([]);
  const [contentSaveCounts, setContentSaveCounts] = useState({});
  const [openSavedContentModal, setOpenSavedContentModal] = useState(false);

  const fetchContentSaves = async (uid) => {
    if (!uid) {
      setSavedContentIds([]);
      setContentSaveCounts({});
      return;
    }

    try {
      const res = await authFetch(`/content-saves?idUser=${uid}`);
      const json = await res.json().catch(() => null);

      if (res.ok && json?.data) {
        setSavedContentIds(json.data.savedContentIds || []);
        setContentSaveCounts(json.data.counts || {});
      }
    } catch (err) {
      console.error("Error cargando contenido guardado", err);
    }
  };

  useEffect(() => {
    fetchContentSaves(userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Guarda/quita un contenido. Actualización optimista (icono + contador),
  // con reversión (recarga real) si el backend falla.
  const toggleContentSave = async (contentId) => {
    if (!userId || !contentId) return;

    const wasSaved = savedContentIds.includes(contentId);

    setSavedContentIds((prev) =>
      wasSaved ? prev.filter((id) => id !== contentId) : [...prev, contentId]
    );
    setContentSaveCounts((prev) => {
      const current = prev[contentId] || 0;
      return { ...prev, [contentId]: Math.max(0, current + (wasSaved ? -1 : 1)) };
    });

    try {
      const res = await authFetch(`/content-saves/${encodeURIComponent(contentId)}/toggle?idUser=${userId}`, {
        method: "PUT",
      });
      const json = await res.json().catch(() => null);

      if (res.ok && json?.data) {
        const { saved, count } = json.data;
        setContentSaveCounts((prev) => ({ ...prev, [contentId]: count }));
        setSavedContentIds((prev) => {
          const has = prev.includes(contentId);
          if (saved && !has) return [...prev, contentId];
          if (!saved && has) return prev.filter((id) => id !== contentId);
          return prev;
        });
      } else {
        await fetchContentSaves(userId);
      }
    } catch (err) {
      console.error("Error guardando contenido", err);
      await fetchContentSaves(userId);
    }
  };

  const savedArticles = CONTENT_ARTICLES.filter((a) => savedContentIds.includes(a.id));

  // Al elegir un contenido guardado en el modal: cierra el modal, filtra la
  // sección de Contenido por su categoría, y baja hasta esa sección.
  const goToSavedContent = (article) => {
    setOpenSavedContentModal(false);
    setContentFilter(article.category);
    window.setTimeout(() => {
      reminderRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  // ===== Contenido de interés =====
  const [contentFilter, setContentFilter] = useState("Todo");
  const filteredArticles = contentFilter === "Todo"
    ? CONTENT_ARTICLES
    : CONTENT_ARTICLES.filter((a) => a.category === contentFilter);

  // ===== ELIMINAR MASCOTAS =====
  const [openDeletePetModal, setOpenDeletePetModal] = useState(false);
  const [petToDelete, setPetToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);

  // Modal límite de mascotas
  const [showPetLimitModal, setShowPetLimitModal] = useState(false);

  // 🔁 fuerza recarga del calendario
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);

  // Estados para edición manual
  const [editedName, setEditedName] = useState(profileName);
  const [editedPhone, setEditedPhone] = useState(profilePhone);
  const [editedLocation, setEditedLocation] = useState(profileLocation);

  const [savingProfile, setSavingProfile] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState("");

  const readUserFromStorage = () => {
    try {
      const raw = localStorage.getItem("petfamUser");
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const resolveUserId = (u) => {
    const id = u?.idUser ?? u?.id ?? u?.userId ?? null;
    return id != null ? Number(id) : null;
  };

  const resetPasswordForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setPasswordSuccess("");
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("Todos los campos son obligatorios.");
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError("La nueva contraseña debe tener al menos 6 caracteres.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("La confirmación no coincide con la nueva contraseña.");
      return;
    }

    if (!userId) {
      setPasswordError("No se pudo identificar al usuario.");
      return;
    }

    try {
      setPasswordLoading(true);

      const res = await authFetch(`/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idUser: userId,
          currentPassword,
          newPassword,
        }),
      });

      const json = await res.json().catch(() => null);

      if (!res.ok || !json || json.status !== 200) {
        setPasswordError(
          json?.userMessage || "No se pudo cambiar la contraseña. Verifica tu contraseña actual."
        );
        return;
      }

      setPasswordSuccess("Tu contraseña se actualizó correctamente.");
      resetPasswordForm();

      setTimeout(() => {
        setOpenPasswordModal(false);
        setPasswordSuccess("");
      }, 1200);
    } catch {
      setPasswordError("Ocurrió un error al conectar con el servidor. Inténtalo de nuevo.");
    } finally {
      setPasswordLoading(false);
    }
  };

  // ====== CARGAR MASCOTAS ======
  const loadPets = async (idUser) => {
    try {
      const res = await authFetch(`/pets/user/${idUser}`);
      const json = await res.json().catch(() => null);

      if (res.ok && json?.data) {
        const list = json.data.map((p) => ({
          id: p.idPet,
          idUser: idUser,
          nombre: p.name,
          tipo: normalizeSpecies(p.species),
          raza: p.breed,
          sexo: p.sex,
          peso: p.weightKg ? `${p.weightKg} kg` : "",
          nacimiento: p.birthDate || "",
          foto: p.cardImageUrl,
          profilePhotoUrl: p.profilePhotoUrl || null,
        }));

        setPets(list);
      } else {
        setPets([]);
      }
    } catch (err) {
      console.error("Error cargando mascotas del usuario", err);
    }
  };

  // ====== SINCRONIZAR USER (SIN PLAN) ======
  const syncUserFromStorage = async () => {
    const local = readUserFromStorage();
    if (!local) return;

    const initialId = resolveUserId(local);
    if (!initialId) return;

    const applyUserToState = (u) => {
      const resolved = resolveUserId(u) ?? initialId;
      setUserId(resolved);

      const nm = u?.name ?? "";
      setProfileName(nm);
      setEditedName(nm);

      const ph = u?.phone ?? "";
      setProfilePhone(ph);
      setEditedPhone(ph);

      const ct = u?.country ?? "";
      setCountry(ct);
      setProfileLocation(ct);
      setEditedLocation(ct);

      if (u?.profilePhotoUrl) {
        const url = u.profilePhotoUrl.startsWith("http") ? u.profilePhotoUrl : `${API_BASE_URL}${u.profilePhotoUrl}`;
        setProfilePhoto(url);
      } else {
        setProfilePhoto(null);
      }

      return resolved;
    };

    const localId = applyUserToState(local);

    try {
      const res = await authFetch(`/user/${initialId}`);
      const json = await res.json().catch(() => null);

      if (res.ok && json?.data) {
        const dbUser = json.data;

        const merged = {
          ...local,
          ...dbUser,
          email: local.email ?? dbUser.email,
          roles: local.roles ?? [],
        };

        localStorage.setItem("petfamUser", JSON.stringify(merged));
        const realId = applyUserToState(merged);

        await loadPets(realId);
        return;
      }

      await loadPets(localId);
    } catch {
      await loadPets(localId);
    }
  };

  useEffect(() => {
    syncUserFromStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onFocus = () => syncUserFromStorage();
    const onVisibility = () => {
      if (document.visibilityState === "visible") syncUserFromStorage();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Foto perfil =====
  const handleProfilePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setProfilePhoto(previewUrl);

    const raw = localStorage.getItem("petfamUser");
    if (!raw) return;
    const user = JSON.parse(raw);

    const id = resolveUserId(user);
    if (!id) {
      console.warn("⚠ Usuario no tiene idUser en el almacenamiento");
      return;
    }

    const formData = new FormData();
    formData.append("photo", file);

    try {
      const res = await authFetch(`/user/${id}/photo`, {
  method: "POST",
  body: formData,
});

      const json = await res.json().catch(() => null);
      if (!json?.data?.profilePhotoUrl) {
        console.error("⚠ Backend no devolvió profilePhotoUrl:", json);
        return;
      }

      const serverUrl = json.data.profilePhotoUrl.startsWith("http")
        ? json.data.profilePhotoUrl
        : `${API_BASE_URL}${json.data.profilePhotoUrl}`;

      setProfilePhoto(serverUrl);

      const updatedUser = {
        ...user,
        ...json.data,
        idUser: json.data.idUser || id,
        profilePhotoUrl: json.data.profilePhotoUrl,
      };

      localStorage.setItem("petfamUser", JSON.stringify(updatedUser));
    } catch (err) {
      console.error("❌ Error subiendo foto:", err);
    }
  };

  useEffect(() => {
    return () => {
      if (profilePhoto && profilePhoto.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(profilePhoto);
        } catch {}
      }
    };
  }, [profilePhoto]);

  // ===== Filtro mascotas =====
  const petsToShow = pets;
  const filteredPets = petsToShow.filter((pet) => {
    if (filter === "todas") return true;
    if (filter === "perros") return pet.tipo === "Perro";
    if (filter === "gatos") return pet.tipo === "Gato";
    return true;
  });

  // ===== Notificaciones derivadas =====
  const unreadCount = notifications.filter((n) => !n.read).length;
  const hasUnread = unreadCount > 0;

  // ⏱️ Cada vez que cambia la lista de notificaciones, registra (si no
  // existía) la hora en que cada id apareció por primera vez. Solo
  // escribe cuando de verdad hay ids nuevos — evita reescribir
  // localStorage en cada render.
  const [notifOrderVersion, setNotifOrderVersion] = useState(0);
  useEffect(() => {
    if (notifications.length === 0) return;

    const map = getNotifFirstSeenMap(userId);
    let changed = false;
    const now = Date.now();

    notifications.forEach((n, idx) => {
      if (map[n.id] == null) {
        // Resta el índice (ms) para que, dentro de un mismo lote generado
        // en el mismo instante, se respete el orden en que ya venían.
        map[n.id] = now - idx;
        changed = true;
      }
    });

    if (changed) {
      setNotifFirstSeenMap(userId, map);
      setNotifOrderVersion((v) => v + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications, userId]);

  // Orden real que se muestra en la campanita: la más nueva (primera vez
  // vista) de primeras, sin importar si está leída o no.
  const sortedNotifications = useMemo(() => {
    const map = getNotifFirstSeenMap(userId);
    return [...notifications].sort((a, b) => (map[b.id] ?? 0) - (map[a.id] ?? 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications, userId, notifOrderVersion]);

  // 🔔 Nombres reales para el pie del panel de notificaciones, calculados
  // a partir de las mascotas cargadas del usuario (ya no es un texto fijo).
  const petNamesLabel = formatPetNamesLabel(pets);

  // 📅 Fecha de hoy para el badge del hero (siempre la fecha real, no un dato fijo)
  const todayLabel = (() => {
    const now = new Date();
    const weekday = now.toLocaleDateString("es-ES", { weekday: "long" });
    const month = now.toLocaleDateString("es-ES", { month: "long" });
    return `${weekday} ${now.getDate()} de ${month}`.toUpperCase();
  })();

  // 🎯 Meta del mes: ahora viene directo del backend junto con las tareas
  // de hoy (o del toggle), no se calcula en el frontend.
  const [monthlyGoalPercent, setMonthlyGoalPercent] = useState(0);

  const goalCopy = getGoalCopy(monthlyGoalPercent);

  // Nombre del mes actual, real (no fijo)
  const currentMonthLabel = (() => {
    const name = new Date().toLocaleDateString("es-ES", { month: "long" });
    return name.charAt(0).toUpperCase() + name.slice(1);
  })();

  // El anillo anima hacia el valor real cada vez que cambia (al entrar a la
  // interfaz, o al completar/desmarcar un cuidado de hoy).
  const [animatedGoalPercent, setAnimatedGoalPercent] = useState(0);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setAnimatedGoalPercent(monthlyGoalPercent));
    return () => cancelAnimationFrame(frame);
  }, [monthlyGoalPercent]);

  const scrollToCalendar = () => {
    const section = document.getElementById("calendar-section");
    if (section) section.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Baja directo a la tarjeta de "Cuidados de hoy" (no solo hasta el
  // calendario) — la usa la tarjeta de meta del mes al hacerle clic.
  const scrollToDailyCare = () => {
    dailyCareRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openPet = (pet) => {
    const normalizedPet = {
      idPet: pet.id || pet.idPet || null,
      idUser: pet.idUser ?? userId ?? null,
      nombre: pet.nombre,
      tipo: pet.tipo,
      raza: pet.raza,
      sexo: pet.sexo,
      peso: pet.peso,
      nacimiento: pet.nacimiento,
      profilePhotoUrl: pet.profilePhotoUrl,
      foto: pet.foto,
    };

    navigate("/informascota", {
      state: { pet: normalizedPet, swipeIn: "up", swipeOut: "down" },
    });
  };

  const handleDateSelect = (date) => {
    console.log("Fecha seleccionada:", date);
  };

  const birthdayEvents = pets.flatMap((p) => {
  if (!p.nacimiento) return [];

  const parsed = parseLocalDateParts(p.nacimiento);
  if (!parsed) return [];

  return [
    {
      id: `birthday-${p.id}`,
      idPet: p.id,
      month: parsed.month,
      day: parsed.day,
      title: `Cumpleaños de ${p.nombre}`,
      type: "birthday",
      petName: p.nombre,
    },
  ];
});

  // 🎂 Notificaciones reales de cumpleaños: si hoy coincide con el
  // cumpleaños de alguna mascota del usuario, se agrega a la campanita
  // con el mismo diseño que las demás notificaciones (sin CTA). Se marca
  // como leída (read: true) si ya se había marcado "✓ Listo" hoy mismo
  // (persistido en localStorage) — así sobrevive a un refresh de página.
  useEffect(() => {
    const today = new Date();
    const todayMonth = today.getMonth();
    const todayDay = today.getDate();

    const readIds = getTodayReadIds(userId);
    const dismissedIds = getTodayDismissedIds(userId);

    const birthdayNotifs = pets.reduce((acc, pet) => {
      const parsed = parseLocalDateParts(pet.nacimiento);
      if (!parsed) return acc;
      if (parsed.month !== todayMonth || parsed.day !== todayDay) return acc;

      const age = today.getFullYear() - parsed.year;
      const id = `birthday-${pet.id}`;
      if (dismissedIds.includes(id)) return acc;

      acc.push({
        id,
        type: "birthday",
        tag: "Celebra",
        petName: pet.nombre,
        time: "Hoy",
        title: age > 0 ? `${pet.nombre} cumple ${age} años hoy 🎂` : `¡Hoy es el cumpleaños de ${pet.nombre}!`,
        text: `Hoy es un día especial para celebrar a ${pet.nombre}. Gracias por cuidarlo con tanto amor 🐾💚`,
        read: readIds.includes(id),
      });

      return acc;
    }, []);

    setNotifications((prev) => {
      const withoutBirthdays = prev.filter((n) => n.type !== "birthday");
      return [...birthdayNotifs, ...withoutBirthdays];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pets, userId]);

  const reminderIndex = getDayNumber() % HEALTH_REMINDERS.length;
  const todayReminder = HEALTH_REMINDERS[reminderIndex];

  // 🩺 Eventos reales de calendario (vacunas/desparasitación) del usuario,
  // desde el mismo endpoint que usa PetfamCalendar. Se recarga cuando
  // cambia el usuario o cuando se refresca el calendario (misma
  // `calendarRefreshKey` que ya usa <PetfamCalendar/>).
  const [calendarHealthEvents, setCalendarHealthEvents] = useState([]);

  useEffect(() => {
    if (!userId) {
      setCalendarHealthEvents([]);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await authFetch(`/calendar/events?idUser=${userId}`);
        const json = await res.json().catch(() => null);
        if (!res.ok) throw new Error("No se pudieron cargar los eventos del calendario");
        const data = Array.isArray(json?.data) ? json.data : [];
        if (!cancelled) setCalendarHealthEvents(data);
      } catch (err) {
        console.error("Error cargando eventos de salud para notificaciones", err);
        if (!cancelled) setCalendarHealthEvents([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, calendarRefreshKey]);

  // 🩺 Notificaciones reales de vacunas y desparasitación: mismo copy y
  // misma cadencia que los correos (2 días antes / mismo día / 1 día
  // después si quedó vencida — VaccineReminderScheduler.java), con el
  // nombre real de la mascota. La desparasitación interna+externa se
  // agrupa en una sola notificación por mascota/fase, igual que el correo.
  useEffect(() => {
    const todayKey = getDateKeyOffset(0);
    const twoDaysKey = getDateKeyOffset(2);
    const overdueKey = getDateKeyOffset(-1);

    const readIds = getReadHealthIds(userId);
    const dismissedIds = getTodayDismissedIds(userId);

    const petNameById = {};
    pets.forEach((p) => {
      const id = p.id ?? p.idPet;
      if (id != null) petNameById[String(id)] = p.nombre;
    });

    const seenDewormingKeys = new Set();
    const healthNotifs = [];

    (calendarHealthEvents || []).forEach((ev) => {
      const dateKeyRaw = ev.eventDate ?? ev.event_date ?? ev.date ?? null;
      const dateKey = dateKeyRaw ? String(dateKeyRaw).slice(0, 10) : null;
      if (!dateKey) return;

      const status = String(ev.status || "").trim().toUpperCase();
      if (status === "HECHO" || status === "DONE") return;

      let phase = null;
      if (dateKey === twoDaysKey) phase = "2days";
      else if (dateKey === todayKey) phase = "today";
      else if (dateKey === overdueKey) phase = "overdue";
      else return;

      const eventType = String(ev.eventType ?? ev.event_type ?? ev.type ?? "").trim().toUpperCase();
      const idPet = ev.idPet ?? ev.id_pet ?? ev.petId ?? null;
      const idEvent = ev.idEvent ?? ev.id_event ?? ev.id ?? null;
      const title = ev.title ?? ev.vaccineName ?? ev.name ?? "";
      const petName = (idPet != null && petNameById[String(idPet)]) || "tu mascota";

      if (eventType === "VACCINE" || eventType === "VACUNA") {
        const id = `health-vaccine-${idEvent}-${phase}`;
        if (dismissedIds.includes(id)) return;

        let notifTitle = "";
        let notifText = "";
        if (phase === "2days") {
          notifTitle = "Ojo 👀, faltan 2 días";
          notifText = `En 2 días toca el siguiente refuerzo de vacuna de ${petName}: ${title}. Te recomiendo agendarlo desde ya.`;
        } else if (phase === "today") {
          notifTitle = "Hoy es el día 💉";
          notifText = `Hoy tiene programada una vacuna ${petName}: ${title}. No lo dejes pasar, es clave para su protección.`;
        } else {
          notifTitle = "Vacuna vencida ⚠️";
          notifText = `Vemos que esta vacuna de ${petName} quedó pendiente y ya pasó su fecha: ${title}. Puedes reprogramar o consultar con nuestro veterinario desde Petfam.`;
        }

        healthNotifs.push({
          id,
          type: "health",
          tag: "Salud",
          petName,
          time: "Hoy",
          title: notifTitle,
          text: notifText,
          ctaLabel: "Ver",
          read: readIds.includes(id),
        });
      } else if (eventType === "DEWORMING_INTERNAL" || eventType === "DEWORMING_EXTERNAL") {
        const dedupeKey = `${idPet}|${phase}`;
        if (seenDewormingKeys.has(dedupeKey)) return;
        seenDewormingKeys.add(dedupeKey);

        const id = `health-deworming-${idPet}-${phase}`;
        if (dismissedIds.includes(id)) return;

        let notifTitle = "";
        let notifText = "";
        if (phase === "2days") {
          notifTitle = "Ojo 👀, faltan 2 días";
          notifText = `En 2 días toca el siguiente refuerzo de desparasitaciones internas y externas de ${petName}. Te recomiendo agendarlo desde ya.`;
        } else if (phase === "today") {
          notifTitle = "Hoy es el día 🪱";
          notifText = `Hoy toca la desparasitación interna y externa de ${petName}. No lo dejes pasar, es clave para su protección.`;
        } else {
          notifTitle = "Desparasitación vencida ⚠️";
          notifText = `Las desparasitaciones internas y externas de ${petName} ya pasaron su fecha programada. Te recomendamos reagendarla cuanto antes para mantener la protección.`;
        }

        healthNotifs.push({
          id,
          type: "health",
          tag: "Salud",
          petName,
          time: "Hoy",
          title: notifTitle,
          text: notifText,
          ctaLabel: "Ver",
          read: readIds.includes(id),
        });
      }
    });

    setNotifications((prev) => {
      const withoutHealth = prev.filter((n) => n.type !== "health");
      return [...healthNotifs, ...withoutHealth];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarHealthEvents, pets, userId]);

  // ⏰ Reloj que revisa cada minuto si cambió la hora, para que las
  // notificaciones de "Cuidados de hoy" aparezcan/desaparezcan en el
  // momento correcto sin que el usuario tenga que recargar la página.
  const [notifClockTick, setNotifClockTick] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNotifClockTick(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  // 📋 Notificaciones reales de "Cuidados de hoy": 7am–3pm avisa que hay
  // tareas pendientes, 3pm–8pm recuerda las que faltan (solo si falta
  // algo), y desde las 8pm muestra el resumen del día. Cada franja
  // reemplaza a la anterior — no se apilan las tres a la vez. La de las
  // 8pm es la excepción: una vez generada, su contenido queda congelado y
  // sigue apareciendo después de medianoche hasta que el usuario la borre
  // (ver deleteNotification más abajo).
  useEffect(() => {
    const now = new Date();
    const hour = now.getHours();
    const todayKey = getTodayDateKey();

    const total = dailyCareTotal;
    const pendingCount = total - dailyCareDoneCount;

    const readIds = getTodayReadIds(userId);
    const dismissedIds = getTodayDismissedIds(userId);

    const taskNotifs = [];

    if (total > 0) {
      if (hour >= 7 && hour < 15) {
        const id = `task-morning-${todayKey}`;
        if (!dismissedIds.includes(id)) {
          taskNotifs.push({
            id,
            type: "task",
            tag: "Hoy",
            petName: petNamesLabel,
            time: "Hoy",
            title: "¡Buenos días! 🐾",
            text: `Hoy tienes ${total} ${total === 1 ? "cuidado" : "cuidados"} pendientes para ${petNamesLabel}. Márcalos a medida que los completes.`,
            ctaLabel: "Ir a la tarea",
            read: readIds.includes(id),
          });
        }
      } else if (hour >= 15 && hour < 20 && pendingCount > 0) {
        const id = `task-afternoon-${todayKey}`;
        if (!dismissedIds.includes(id)) {
          taskNotifs.push({
            id,
            type: "task",
            tag: "Hoy",
            petName: petNamesLabel,
            time: "Hoy",
            title: "Aún te faltan tareas",
            text: `Te ${pendingCount === 1 ? "falta" : "faltan"} ${pendingCount} ${pendingCount === 1 ? "cuidado" : "cuidados"} para cerrar el día completo con ${petNamesLabel}.`,
            ctaLabel: "Ir a la tarea",
            read: readIds.includes(id),
          });
        }
      }
    }

    let eveningList = getStoredEveningTaskNotifs(userId);
    const todayEveningId = `task-evening-${todayKey}`;
    const alreadyHasTodayEvening = eveningList.some((n) => n.id === todayEveningId);

    if (!alreadyHasTodayEvening && total > 0 && hour >= 20) {
      const allDone = pendingCount <= 0;
      const newEveningNotif = {
        id: todayEveningId,
        type: "task",
        tag: "Hoy",
        petName: petNamesLabel,
        time: "Hoy",
        title: allDone ? "¡Día completo! 🎉" : "No completaste tus tareas de hoy",
        text: allDone
          ? `Completaste todos los cuidados de hoy con ${petNamesLabel}. ¡Sigue así!`
          : `Te ${pendingCount === 1 ? "quedó" : "quedaron"} ${pendingCount} ${pendingCount === 1 ? "cuidado" : "cuidados"} sin marcar hoy con ${petNamesLabel}.`,
        ctaLabel: "Ir a la tarea",
        read: false,
      };
      eveningList = addStoredEveningTaskNotif(userId, newEveningNotif);
    }

    // Más reciente primero (el id trae la fecha, así que ordena solo)
    const sortedEveningList = [...eveningList].sort((a, b) => (a.id < b.id ? 1 : -1));
    sortedEveningList.forEach((n) => {
      taskNotifs.push({ ...n, read: !!n.read });
    });

    setNotifications((prev) => {
      const withoutTasks = prev.filter((n) => n.type !== "task");
      return [...taskNotifs, ...withoutTasks];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyCareTasks, pets, userId, notifClockTick]);

  // 🏆 Notificación de racha ("Logro"): usa el streakCount real que ya
  // devuelve el backend en /daily-care/today, y los nombres reales de las
  // mascotas. Aparece exactamente cuando la racha llega a 8, 15 o 30 —
  // como la racha sube de a uno por día, deja de coincidir sola al día
  // siguiente, así que no hace falta lógica extra para que desaparezca.
  // Sin botón, tal como se pidió.
  useEffect(() => {
    const STREAK_MILESTONES = [8, 15, 30];

    const readIds = getTodayReadIds(userId);
    const dismissedIds = getTodayDismissedIds(userId);
    const achievementNotifs = [];

    if (STREAK_MILESTONES.includes(streakCount)) {
      const id = `achievement-streak-${streakCount}`;

      if (!dismissedIds.includes(id)) {
        let text = "";
        if (streakCount === 8) {
          text = "¡Vas una semana seguida! Una semana más y llegas a 15 días de racha.";
        } else if (streakCount === 15) {
          text = "¡Dos semanas seguidas cuidando con amor! Sigue así para llegar a los 30 días.";
        } else {
          text = "¡Un mes entero sin fallar un solo día! Racha impresionante 🏆";
        }

        achievementNotifs.push({
          id,
          type: "achievement",
          tag: "Logro",
          petName: petNamesLabel,
          time: "Hoy",
          title: `${streakCount} días seguidos cuidando a ${petNamesLabel}`,
          text,
          read: readIds.includes(id),
        });
      }
    }

    setNotifications((prev) => {
      const withoutAchievements = prev.filter((n) => n.type !== "achievement");
      return [...achievementNotifs, ...withoutAchievements];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streakCount, pets, userId]);

  // 📚 Notificación de "nuevo contenido subido" — sale de la plantilla
  // NEW_CONTENT_NOTIFICATION definida arriba del componente. No se limpia
  // a medianoche (puede seguir sin leer varios días); solo desaparece si
  // el usuario le da "✕", o si cambias el `id` de la plantilla.
  useEffect(() => {
    const contentNotifs = [];
    const cfg = NEW_CONTENT_NOTIFICATION;

    if (cfg && cfg.id) {
      const dismissedIds = getPermanentContentIds("dismissed", userId);

      if (!dismissedIds.includes(cfg.id)) {
        const readIds = getPermanentContentIds("read", userId);

        contentNotifs.push({
          id: cfg.id,
          type: "content",
          tag: "Descubre",
          petName: null,
          time: "Hoy",
          title: cfg.title,
          text: cfg.text,
          ctaLabel: "Leer ahora",
          targetCategory: cfg.targetCategory || "Todo",
          read: readIds.includes(cfg.id),
        });
      }
    }

    setNotifications((prev) => {
      const withoutContent = prev.filter((n) => n.type !== "content");
      return [...contentNotifs, ...withoutContent];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // 🔋 Notificación de "energía": se dispara cuando el % real de cuidados
  // de una mascota (el mismo dato de la barra SALUD, /pets/{id}/care-percent)
  // cambia respecto al último valor visto. La primera vez que vemos el dato
  // de una mascota solo se guarda como línea base (sin notificar), para no
  // avisar de la nada al cargar la página por primera vez.
  useEffect(() => {
    if (!userId || !pets || pets.length === 0) return;

    const lastKnown = getLastKnownCarePercents(userId);
    const updatedLastKnown = { ...lastKnown };
    const todayKey = getTodayDateKey();
    let baselineChanged = false;

    pets.slice(0, 2).forEach((pet) => {
      const petId = String(pet.id);
      const current = petCarePercents[petId];
      if (current == null) return;

      const previous = lastKnown[petId];
      updatedLastKnown[petId] = current;

      if (previous == null) {
        baselineChanged = true;
        return;
      }
      if (previous === current) return;

      baselineChanged = true;
      const direction = current > previous ? "up" : "down";
      const id = `energy-${petId}-${todayKey}-${direction}`;

      addStoredEnergyNotif(userId, {
        id,
        type: "energy",
        tag: "Salud",
        petName: pet.nombre,
        time: "Hoy",
        title:
          direction === "up"
            ? `La energía de ${pet.nombre} subió a ${current}%`
            : `La energía de ${pet.nombre} bajó a ${current}%`,
        text:
          direction === "up"
            ? "Vas muy bien marcando sus cuidados este mes. ¡Sigue así!"
            : "Este mes le faltan cuidados por marcar. Revisa su perfil para ponerte al día.",
        ctaLabel: `Ver a ${pet.nombre}`,
        idPet: pet.id,
        read: false,
      });
    });

    if (baselineChanged) {
      setLastKnownCarePercents(userId, updatedLastKnown);
    }

    const storedEnergy = getStoredEnergyNotifs(userId);
    const sortedEnergy = [...storedEnergy].sort((a, b) => (a.id < b.id ? 1 : -1));

    setNotifications((prev) => {
      const withoutEnergy = prev.filter((n) => n.type !== "energy");
      return [...sortedEnergy.map((n) => ({ ...n, read: !!n.read })), ...withoutEnergy];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petCarePercents, pets, userId]);

  // ✅ YA NO bloquea por plan
  const handleAddPetClick = () => {
    if (pets.length >= 2) setShowPetLimitModal(true);
    else navigate("/anadirmascota", { state: { transition: "panel" } });
  };

  const handleRemovePetClick = () => {
    setDeleteError("");
    setOpenDeletePetModal(true);
  };

  const askConfirmDelete = (pet) => {
    setDeleteError("");
    setPetToDelete(pet);
    setOpenConfirmDelete(true);
  };

  const doDeletePet = async () => {
    if (!petToDelete?.id) return;

    setDeleteLoading(true);
    setDeleteError("");

    try {
      const res = await authFetch(`/pets/${petToDelete.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("No se pudo eliminar la mascota");

      setPets((prev) => prev.filter((p) => p.id !== petToDelete.id));
      if (userId) await loadPets(userId);

      setOpenConfirmDelete(false);
      setOpenDeletePetModal(false);
      setPetToDelete(null);

      setCalendarRefreshKey((k) => k + 1);
    } catch {
      setDeleteError("No se pudo eliminar la mascota. Verifica el backend o el endpoint DELETE.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleAcceptExtraPet = () => {
    setShowPetLimitModal(false);
    navigate("/anadirmascota", { state: { transition: "panel", extraPet: true } });
  };

  // ✅ Dr. Paws (sin bloqueo)
  const openDrPawsForPet = (pet, mode = "CHAT") => {
    if (!pet) return;

    const normalizedPet = {
      idPet: pet.idPet ?? pet.id ?? pet.id_pet ?? null,
      name: pet.name ?? pet.nombre,
      nombre: pet.nombre ?? pet.name,
      species: pet.species ?? pet.tipo,
      birthDate: pet.birthDate ?? pet.nacimiento,
      idUser: pet.idUser ?? userId ?? null,
      mode,
    };

    setChatPet(normalizedPet);
    setOpenVetChat(true);
  };

  const handleOpenDrPaws = (petId) => {
    if (!pets || pets.length === 0) {
      alert("Primero crea una mascota para que laa Dra.Vetia pueda ayudarte");
      return;
    }

    if (petId != null) {
      const found = (pets || []).find((p) => String(p.id) === String(petId));
      if (found) {
        openDrPawsForPet(found, "CHAT");
        return;
      }
    }

    if (pets.length === 1) {
      openDrPawsForPet(pets[0], "CHAT");
      return;
    }

    setDrPawsMode("CHAT");
    setOpenDrPawsPicker(true);
  };

  const handlePickPetForDrPaws = (pet) => {
    setOpenDrPawsPicker(false);
    openDrPawsForPet(pet, drPawsMode);
  };

  // ===== ✅ Cita de etología (Medismart) =====
  // Antes esta acción vivía en Informascota.jsx ("Solicitar consulta").
  // Ahora se dispara directamente desde la tarjeta "Cita de etología" del inicio.
  const [showMedismart, setShowMedismart] = useState(false);

  const getMedismartLastPetKey = (idUser) =>
    idUser ? `petfam:lastPetId:${idUser}` : "petfam:lastPetId";

  const saveMedismartLastPetId = (idUser, idPet) => {
    const n = Number(idPet);
    if (!Number.isFinite(n) || n <= 0) return;
    try {
      localStorage.setItem(getMedismartLastPetKey(idUser), String(n));
      localStorage.setItem(`${getMedismartLastPetKey(idUser)}:ts`, String(Date.now()));
    } catch {}
  };

  const nextPaint = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));

  const openMedismartModal = async () => {
    setShowMedismart(true);

    const idUser = userId ?? resolveUserId(readUserFromStorage());

    if (!idUser) {
      alert("No encontramos tu sesión. Inicia sesión nuevamente.");
      setShowMedismart(false);
      return;
    }

    // ✅ si el usuario tiene mascotas registradas, se envía la primera como referencia
    const currentPetId = Number(pets?.[0]?.id) || null;
    saveMedismartLastPetId(idUser, currentPetId);

    let redirected = false;

    try {
      const endpoint = `${API_BASE_URL}/api/medismart/sso?idUser=${idUser}&idPet=${currentPetId}`;
      const res = await fetch(endpoint, { method: "GET" });

      const text = await res.text();

      let json;
      try {
        json = JSON.parse(text);
      } catch {
        alert("El backend no devolvió JSON válido. Revisa consola.");
        return;
      }

      const url = json?.data?.url;
      if (!url) {
        alert(json?.userMessage || "No llegó URL SSO desde backend.");
        return;
      }

      await nextPaint();
      await new Promise((r) => setTimeout(r, 120));

      redirected = true;
      window.location.assign(url);
    } catch (err) {
      console.error("❌ ERROR FETCH MEDISMART:", err);
      alert("Error contactando tu backend. Intenta otra vez.");
    } finally {
      if (!redirected) setShowMedismart(false);
    }
  };

  // ===== Header: navegación por secciones (Inicio / Tus mascotas / Calendario / Contenido) =====
  const handleNavClick = (key) => {
    setActiveNav(key);
    setOpenMenu(false);
    setOpenNotifications(false);
    setOpenMobileNav(false);

    if (key === "inicio") heroRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (key === "mascotas") topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (key === "calendario") calendarRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (key === "contenido") reminderRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // ===== Header: notificaciones =====
  const notifIcon = (type) => {
    if (type === "birthday") return <Cake size={18} strokeWidth={2} />;
    if (type === "health" || type === "energy") return <Stethoscope size={18} strokeWidth={2} />;
    if (type === "achievement") return <Trophy size={18} strokeWidth={2} />;
    if (type === "content") return <Sparkles size={18} strokeWidth={2} />;
    return <Clock size={18} strokeWidth={2} />;
  };

  const handleNotifCta = (n) => {
    markNotificationRead(n.id);
    setOpenNotifications(false);
    if (n.type === "birthday") {
      navigate("/tu-plan");
    } else if (n.type === "content") {
      setContentFilter(n.targetCategory || "Todo");
      window.setTimeout(() => {
        reminderRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
    } else if (n.type === "energy") {
      const targetPet = (pets || []).find((p) => String(p.id) === String(n.idPet));
      if (targetPet) {
        openPet(targetPet);
      } else {
        scrollToCalendar();
      }
    } else {
      scrollToCalendar();
    }
  };

  const markAllNotificationsRead = () => {
    setNotifications((prev) =>
      prev.map((n) => {
        if (n.type === "birthday") addTodayReadId(userId, n.id);
        if (n.type === "health") addReadHealthId(userId, n.id);
        if (n.type === "achievement") addTodayReadId(userId, n.id);
        if (n.type === "content") addPermanentContentId("read", userId, n.id);
        if (n.type === "energy") updateStoredEnergyNotifRead(userId, n.id);
        if (n.type === "task") {
          if (n.id.startsWith("task-evening-")) {
            updateStoredEveningTaskNotifRead(userId, n.id);
          } else {
            addTodayReadId(userId, n.id);
          }
        }
        return { ...n, read: true };
      })
    );
  };

  const markNotificationRead = (id) => {
    setNotifications((prev) =>
      prev.map((n) => {
        if (n.id !== id) return n;
        if (n.type === "birthday") addTodayReadId(userId, id);
        if (n.type === "health") addReadHealthId(userId, id);
        if (n.type === "achievement") addTodayReadId(userId, id);
        if (n.type === "content") addPermanentContentId("read", userId, id);
        if (n.type === "energy") updateStoredEnergyNotifRead(userId, id);
        if (n.type === "task") {
          if (id.startsWith("task-evening-")) {
            updateStoredEveningTaskNotifRead(userId, id);
          } else {
            addTodayReadId(userId, id);
          }
        }
        return { ...n, read: true };
      })
    );
  };

  // ✕ Borra una notificación de la campanita. Para las reales de "hoy"
  // (cumpleaños, salud, logro, y las de tareas de las 7am/3pm), que se
  // recalculan solas cada vez que cambian `pets`/el calendario/la hora, se
  // persiste como "descartada hoy" para que no vuelva a aparecer en lo que
  // queda del día. Tres excepciones con memoria permanente (no ligada al
  // día de hoy): las de las 8pm (una por fecha, cada una se borra aparte),
  // la de "nuevo contenido" (sigue viva hasta que la borres o cambies su
  // `id` en la plantilla), y la de "energía" (una por cada cambio real de
  // % detectado, cada una se borra aparte).
  const deleteNotification = (n) => {
    const id = n.id;
    if (id.startsWith("task-evening-")) {
      removeStoredEveningTaskNotif(userId, id);
    } else if (n.type === "content") {
      addPermanentContentId("dismissed", userId, id);
    } else if (n.type === "energy") {
      removeStoredEnergyNotif(userId, id);
    } else {
      addTodayDismissedId(userId, id);
    }
    setNotifications((prev) => prev.filter((item) => item.id !== id));
  };

  const onlyDigits = (s) => String(s || "").replace(/\D/g, "");
const formatCardNumber = (value) => {
  const d = onlyDigits(value).slice(0, 16);
  return d.replace(/(\d{4})(?=\d)/g, "$1 ");
};

function normalizeCardHolder(value) {
  return String(value || "")
    .replace(/[^A-Za-zÁÉÍÓÚÜÑáéíóúüñ\s]/g, "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trimStart();
}

// (Opcional) si quieres que *todo texto* se vuelva mayúscula automáticamente:
function toUpperText(value) {
  return String(value || "").toUpperCase();
}

const wompiTokenizeEndpoint = () => {
  // ✅ QA -> sandbox, PROD -> production
  return IS_QA ? "https://sandbox.wompi.co/v1/tokens/cards" : "https://production.wompi.co/v1/tokens/cards";
};

const getUserFromStorage = () => {
  try {
    const raw = localStorage.getItem("petfamUser");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const handleSavePaymentSource = async () => {
  try {
    setPayLoading(true);
    setPayError("");
    setPaySuccess(false);

    const u = getUserFromStorage();
    const email = (u?.email || "").toString().trim().toLowerCase();
    const id = resolveUserId(u);

    if (!id) throw new Error("No se pudo identificar el usuario (idUser).");
    if (!email) throw new Error("No encontré el email del usuario.");

    // 1) merchant (publicKey + tokens)
    const mRes = await authFetch(`/wompi/merchant`);
    const merchant = await mRes.json().catch(() => null);
    if (!mRes.ok || !merchant?.publicKey) {
      throw new Error(merchant?.message || "No se pudo obtener merchant de Wompi.");
    }

    const publicKey = merchant.publicKey;
    const acceptanceToken = merchant?.acceptance?.token;
    const personalAuthToken = merchant?.personalData?.token;

    if (!acceptanceToken || !personalAuthToken) {
      throw new Error("Wompi merchant no devolvió tokens de aceptación.");
    }

    // 2) Tokenizar tarjeta (tok_...)
    const payloadToken = {
      number: onlyDigits(cardNumber),
      cvc: onlyDigits(cardCvc),
      exp_month: onlyDigits(cardExpMonth),
      exp_year: onlyDigits(cardExpYear),
      card_holder: String(cardHolder || "").trim(),
    };

    if (!payloadToken.card_holder) throw new Error("El nombre del titular es obligatorio.");
    if (payloadToken.number.length < 13) throw new Error("Número de tarjeta inválido.");
    if (payloadToken.cvc.length < 3) throw new Error("CVC inválido.");
    if (payloadToken.exp_month.length < 1 || Number(payloadToken.exp_month) < 1 || Number(payloadToken.exp_month) > 12) {
      throw new Error("Mes de expiración inválido.");
    }
    if (payloadToken.exp_year.length < 2) throw new Error("Año de expiración inválido.");

    const tRes = await fetch(wompiTokenizeEndpoint(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${publicKey}`,
      },
      body: JSON.stringify(payloadToken),
    });

    const tJson = await tRes.json().catch(() => null);
    const token = tJson?.data?.id;

    if (!tRes.ok || !token) {
      const msg = tJson?.error?.messages ? JSON.stringify(tJson.error.messages) : (tJson?.error?.reason || tJson?.message || "Wompi rechazó la tokenización.");
      throw new Error(msg);
    }

    // 3) Crear Payment Source en backend (usa tus tokens si quieres enviarlos, o dejas que backend los obtenga)
    const psRes = await authFetch(`/wompi/payment-sources/card`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token,
        customerEmail: email,

        // ✅ opcional: si los mandas, evitas un fetch extra en backend
        acceptanceToken,
        acceptPersonalAuthToken: personalAuthToken,
      }),
    });

    const psJson = await psRes.json().catch(() => null);
    if (!psRes.ok || !psJson?.ok) {
      throw new Error(psJson?.message || "No se pudo crear la fuente de pago.");
    }

    const paymentSourceId = psJson?.data?.paymentSourceId;
    if (!paymentSourceId) throw new Error("Wompi no devolvió paymentSourceId.");

    // 4) Guardar paymentSourceId en tu usuario
    const upRes = await authFetch(`/user/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ paymentSourceId }),
    });

    const upJson = await upRes.json().catch(() => null);
    if (!upRes.ok || upJson?.status !== 200) {
      throw new Error(upJson?.userMessage || "No se pudo guardar la fuente de pago en el usuario.");
    }

    // 5) Persistir en localStorage (para que se refleje sin recargar)
    const updatedUser = {
      ...u,
      paymentSourceId,
    };
    localStorage.setItem("petfamUser", JSON.stringify(updatedUser));

    setPaySuccess(true);
    setTimeout(() => setPaySuccess(false), 1500);

    // limpiar campos sensibles (no guardar tarjeta en memoria)
    setCardHolder("");
    setCardNumber("");
    setCardExpMonth("");
    setCardExpYear("");
    setCardCvc("");

    // cerrar modal
    setTimeout(() => setOpenPaymentModal(false), 600);
  } catch (e) {
    setPayError(e?.message || "Error actualizando información de pago.");
  } finally {
    setPayLoading(false);
  }
};

  return (
    <>
      {/* HEADER — fuera de .page-enter a propósito: si ese contenedor
          tiene una animación de transición de ruta con transform (algo
          común en clases llamadas "page-enter"), un transform en un
          ancestro rompe el position:fixed de sus hijos. Al vivir afuera,
          el header queda anclado a la ventana pase lo que pase adentro. */}
      <header className="header">
        <div className="header-container">
          <div className="nav-left">
            <img src={petfamLogo} className="logo" alt="Logo Petfam" />
          </div>

          <nav className="nav-center">
            <a
              href="#inicio"
              className={`nav-link ${activeNav === "inicio" ? "active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                handleNavClick("inicio");
              }}
            >
              Inicio
            </a>
            <a
              href="#pets-section"
              className={`nav-link ${activeNav === "mascotas" ? "active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                handleNavClick("mascotas");
              }}
            >
              Tus mascotas
            </a>
            <a
              href="#calendar-section"
              className={`nav-link ${activeNav === "calendario" ? "active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                handleNavClick("calendario");
              }}
            >
              Calendario
            </a>
            <a
              href="#contenido"
              className={`nav-link ${activeNav === "contenido" ? "active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                handleNavClick("contenido");
              }}
            >
              Contenido
            </a>
          </nav>

          <div className="nav-right">
            {/* ☰ Menú móvil */}
            <div className="mobile-menu-wrap" ref={mobileNavRef}>
              <button
                type="button"
                className="mobile-menu-btn"
                onClick={() => setOpenMobileNav((v) => !v)}
                aria-label="Abrir menú"
                aria-haspopup="true"
                aria-expanded={openMobileNav}
              >
                <Menu size={20} strokeWidth={2} />
              </button>

              {openMobileNav && (
                <nav className="mobile-nav-panel" aria-label="Navegación principal">
                  <a
                    href="#inicio"
                    className={`mobile-nav-link ${activeNav === "inicio" ? "active" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick("inicio");
                    }}
                  >
                    Inicio
                  </a>
                  <a
                    href="#pets-section"
                    className={`mobile-nav-link ${activeNav === "mascotas" ? "active" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick("mascotas");
                    }}
                  >
                    Tus mascotas
                  </a>
                  <a
                    href="#calendar-section"
                    className={`mobile-nav-link ${activeNav === "calendario" ? "active" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick("calendario");
                    }}
                  >
                    Calendario
                  </a>
                  <a
                    href="#contenido"
                    className={`mobile-nav-link ${activeNav === "contenido" ? "active" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick("contenido");
                    }}
                  >
                    Contenido
                  </a>
                </nav>
              )}
            </div>

            {/* 🔔 Notificaciones */}
            <div className="notif-wrap" ref={notifRef}>
              <button
                type="button"
                className={`icon-bell ${hasUnread ? "icon-bell-shake" : ""}`}
                onClick={() => setOpenNotifications((v) => !v)}
                aria-label="Notificaciones"
                aria-haspopup="true"
                aria-expanded={openNotifications}
              >
                <Bell size={19} strokeWidth={1.8} />
                {hasUnread && <span className="bell-dot" aria-hidden="true" />}
              </button>

              {openNotifications && (
                <div className="notif-panel" role="menu">
                  <div className="notif-panel-head">
                    <div>
                      <span className="notif-eyebrow">Novedades de tu familia</span>
                      <h4 className="notif-title">
                        {unreadCount > 0 ? `${unreadCount} cosas por ver` : "Todo al día"}
                      </h4>
                    </div>
                    <button type="button" className="notif-mark-read" onClick={markAllNotificationsRead}>
                      Marcar leídas
                    </button>
                  </div>

                  <div className="notif-list">
                    {sortedNotifications.map((n) => (
                      <div className={`notif-item notif-${n.type} ${n.read ? "is-read" : ""}`} key={n.id}>
                        <div className="notif-icon">{notifIcon(n.type)}</div>

                        <div className="notif-body">
                          <div className="notif-meta">
                            <span className="notif-tag">
                              {n.petName ? `${n.tag} · ${n.petName}` : n.tag}
                            </span>
                            <span className="notif-time-wrap">
                              <span className="notif-time">{n.time}</span>
                              <button
                                type="button"
                                className="notif-delete"
                                aria-label="Eliminar notificación"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteNotification(n);
                                }}
                              >
                                <X size={12} />
                              </button>
                            </span>
                          </div>

                          <h5 className="notif-item-title">{n.title}</h5>
                          <p className="notif-item-text">{n.text}</p>

                          <div className="notif-actions">
                            {n.ctaLabel && (
                              <button type="button" className="notif-cta" onClick={() => handleNotifCta(n)}>
                                {n.ctaLabel}
                              </button>
                            )}
                            {!n.read && (
                              <button
                                type="button"
                                className="notif-done"
                                onClick={() => markNotificationRead(n.id)}
                              >
                                ✓ Listo
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="notif-footer">PETFAM CUIDA A {petNamesLabel}</div>
                </div>
              )}
            </div>

            {/* 👤 Usuario */}
            <div className="user-menu" ref={menuRef}>
              <button
                className="icon-user"
                onClick={toggleMenu}
                aria-label="Perfil"
                aria-haspopup="menu"
                aria-expanded={openMenu}
              >
                <span className="user-avatar-badge">
                  {profilePhoto ? (
                    <img src={profilePhoto} alt="" />
                  ) : (
                    <span>{(profileName || "?").trim().charAt(0).toUpperCase() || "?"}</span>
                  )}
                </span>
                <span className="user-name-label">
                  {profileName ? toTitleCaseName(profileName).split(" ")[0] : "Perfil"}
                </span>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="arrow-svg">
                  <path fillRule="evenodd" d="M12.53 16.28a.75.75 0 0 1-1.06 0l-7.5-7.5a.75.75 0 0 1 1.06-1.06L12 14.69l6.97-6.97a.75.75 0 1 1 1.06 1.06l-7.5 7.5Z" clipRule="evenodd" />
                </svg>
              </button>

              {openMenu && (
                <div className="dropdown-menu" role="menu">
                  <Link
                    to={location.pathname}
                    role="menuitem"
                    onClick={(e) => {
                      e.preventDefault();
                      setOpenMenu(false);
                      setOpenProfilePanel(true);
                    }}
                  >
                    <Pencil size={20} style={{ marginRight: 10 }} strokeWidth={1.8} />
                    Editar perfil
                  </Link>

                   <button
      type="button"
      role="menuitem"
      className="dropdown-menu-btn"
      onClick={() => {
        setOpenMenu(false);
        setOpenVideoGuide(true);
      }}
    >
      <PlayCircle size={20} style={{ marginRight: 10 }} strokeWidth={1.8} />
      Video guiado
    </button>

                  <button
      type="button"
      role="menuitem"
      className="dropdown-menu-btn"
      onClick={() => {
        setOpenMenu(false);
        setOpenSavedContentModal(true);
      }}
    >
      <Bookmark size={20} style={{ marginRight: 10 }} strokeWidth={1.8} />
      Contenido guardado
    </button>

                  <hr />

                  <Link
                    to="/"
                    state={{ from: "interfaz" }}
                    role="menuitem"
                    onClick={() => {
                      localStorage.removeItem("petfamUser");
                      localStorage.removeItem("idUser");
                      localStorage.removeItem("token");
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="20" height="20" style={{ marginRight: 10 }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6A2.25 2.25 0 0 0 5.25 5.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                    </svg>
                    Salir
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="page-enter">
        {/* HERO */}
        <section className="herointerfaz" ref={heroRef} id="inicio">
          <div className="hero-inner">
            <div className="hero-date-badge">
              <Sparkles size={14} strokeWidth={2.2} />
              {todayLabel}
            </div>

            <div className="hero-grid">
              <div className="hero-copy">
                <div className="greet-block">
                  <label className={`avatar ${profilePhoto ? "has-photo" : ""}`}>
                    <input type="file" accept="image/*" onChange={handleProfilePhotoChange} />
                    {profilePhoto ? (
                      <>
                        <span className="avatar-img" style={{ backgroundImage: `url(${profilePhoto})` }} />
                        <span className="avatar-grad" aria-hidden="true" />
                      </>
                    ) : (
                      <>
                        <span className="avatar-placeholder">+</span>
                        <span className="avatar-msg">Añadir tu foto</span>
                      </>
                    )}
                  </label>

                  <h1 className="saludo saludo-animated">
                    {profileName ? `¡Hola, ${toTitleCaseName(profileName)}!` : "¡Hola!"}
                  </h1>
                </div>

                <p className="hero-lead">
                  Revisa el cuidado de tus mascotas, agenda citas y habla con nuestro
                  equipo cuando lo necesites.
                </p>
              </div>

              <div className="hero-side">
                <div
                  className="goal-card"
                  role="button"
                  tabIndex={0}
                  onClick={scrollToDailyCare}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      scrollToDailyCare();
                    }
                  }}
                >
                  {/* 🔥 Racha: badge en la esquina, no compite con el % */}
                  <div className="goal-streak-badge">
                    <Flame size={14} strokeWidth={2.4} />
                    <span>{streakCount} {streakCount === 1 ? "día" : "días"} de racha</span>
                  </div>

                  <div className="goal-ring">
                    <svg viewBox="0 0 100 100" className="goal-ring-svg" aria-hidden="true">
                      <circle cx="50" cy="50" r="42" className="goal-ring-track" />
                      <circle
                        cx="50"
                        cy="50"
                        r="42"
                        className="goal-ring-progress"
                        style={{
                          strokeDasharray: `${2 * Math.PI * 42}`,
                          strokeDashoffset: `${2 * Math.PI * 42 * (1 - animatedGoalPercent / 100)}`,
                        }}
                      />
                    </svg>
                    <div className="goal-ring-value">
                      <strong>{monthlyGoalPercent}%</strong>
                      <span>Cuidado</span>
                    </div>
                  </div>

                  <div className="goal-card-text">
                    <span className="goal-eyebrow">Meta de {currentMonthLabel}</span>
                    <h4 className="goal-title">{goalCopy.title}</h4>
                    <p className="goal-desc">{goalCopy.desc}</p>
                  </div>
                </div>

                <div className="quick-actions">
                  <button
                    type="button"
                    className="quick-card quick-card-dark"
                    onClick={() => handleOpenDrPaws()}
                  >
                    <Phone size={20} strokeWidth={2} />
                    <strong>Call Center 24/7</strong>
                    <span>Habla con un veterinario en cualquier momento.</span>
                  </button>

                  <button
                    type="button"
                    className="quick-card quick-card-light"
                    onClick={openMedismartModal}
                  >
                    <CalendarHeart size={20} strokeWidth={2} />
                    <strong>Cita con Etólogo</strong>
                    <span>Consulta con un especialista en alianza con Medismart.life.</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECCIÓN MASCOTAS + CALENDARIO */}
        <section className="pets-section" aria-labelledby="pets-title" ref={topRef} id="pets-section">
          <div className="pets-calendar-layout">
            {/* COLUMNA IZQUIERDA */}
            <div className="left-pets-column">
              <div className="pets-header-row">
                <div className="pets-heading-v2">
                  <h2 id="pets-title" className="pets-title-v2">Tus mascotas</h2>
                  <p className="pets-subtitle-v2">Selecciona a tus mascotas para ver su información.</p>
                </div>

                <div className="pets-filter-pills" role="tablist" aria-label="Filtrar mascotas">
                  <button
                    role="tab"
                    aria-selected={filter === "todas"}
                    className={`filter-pill ${filter === "todas" ? "is-active" : ""}`}
                    onClick={() => setFilter("todas")}
                  >
                    <Sparkles size={15} strokeWidth={2.2} />
                    <span>Todos</span>
                  </button>

                  <button
                    role="tab"
                    aria-selected={filter === "perros"}
                    className={`filter-pill ${filter === "perros" ? "is-active" : ""}`}
                    onClick={() => setFilter("perros")}
                  >
                    <Dog size={15} strokeWidth={2.2} />
                    <span>Perros</span>
                  </button>

                  <button
                    role="tab"
                    aria-selected={filter === "gatos"}
                    className={`filter-pill ${filter === "gatos" ? "is-active" : ""}`}
                    onClick={() => setFilter("gatos")}
                  >
                    <Cat size={15} strokeWidth={2.2} />
                    <span>Gatos</span>
                  </button>
                </div>
              </div>

              {/* Tarjetas de mascotas */}
              <div className="pets-grid-v2">
                {(() => {
                  const featuredId = petsToShow[0]?.id;
                  return filteredPets.map((pet) => {
                    const isFeatured = pet.id === featuredId;
                    const apiCarePercent = petCarePercents[String(pet.id)];
                    const health =
                      pet.health ??
                      (apiCarePercent != null ? apiCarePercent : getPetHealthPercent(pet));
                    const nextEvent = pet.footer || getPetNextEvent(pet);
                    const badgeLabel = pet.badge || [pet.raza, pet.tipo].filter(Boolean).join(" · ").toUpperCase() || (pet.tipo || "").toUpperCase();

                    return (
                    <div
                      key={pet.id}
                      className={`pet-card-v2 ${isFeatured ? "pet-card-v2-dark" : "pet-card-v2-light"}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => openPet(pet)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openPet(pet);
                        }
                      }}
                    >
                      <div className="pet-card-v2-photo">
                        <img
                          src={pet.foto || getBreedImage(pet.tipo, pet.raza)}
                          alt={pet.nombre}
                        />
                        <button className="pet-card-v2-arrow" aria-hidden="true" tabIndex={-1}>
                          <ArrowUpRight size={18} strokeWidth={2.4} />
                        </button>
                      </div>

                      <span className="pet-card-v2-badge">{badgeLabel}</span>

                      <h3 className="pet-card-v2-name">{pet.nombre}</h3>

                      <div className="pet-card-v2-health">
                        <div className="pet-card-v2-health-row">
                          <span>CUIDADOS</span>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            {health}%
                            <button
                              type="button"
                              ref={(el) => {
                                healthInfoBtnRefs.current[pet.id] = el;
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenHealthInfoPetId((prev) => (prev === pet.id ? null : pet.id));
                              }}
                              aria-label="¿Cómo se calcula este porcentaje?"
                              style={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                border: "1px solid currentColor",
                                background: "transparent",
                                color: "inherit",
                                fontSize: 10,
                                lineHeight: "14px",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 0,
                                opacity: 0.85,
                                flexShrink: 0,
                              }}
                            >
                              ?
                            </button>
                          </span>
                        </div>
                        <div className="pet-card-v2-health-bar">
                          <div className="pet-card-v2-health-fill" style={{ width: `${health}%` }} />
                        </div>
                      </div>

                      {openHealthInfoPetId === pet.id &&
                        healthInfoAnchorRect &&
                        createPortal(
                          <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              position: "fixed",
                              top: healthInfoAnchorRect.bottom + 10,
                              left: Math.min(
                                Math.max(healthInfoAnchorRect.left + healthInfoAnchorRect.width / 2 - 110, 12),
                                window.innerWidth - 232
                              ),
                              width: 220,
                              zIndex: 9999,
                              background: "#fff",
                              color: "#0B2F3E",
                              borderRadius: 14,
                              padding: "12px 14px",
                              fontSize: 12,
                              fontWeight: 500,
                              lineHeight: 1.4,
                              textAlign: "center",
                              boxShadow: "0 14px 34px rgba(0,0,0,.28)",
                            }}
                          >
                            Se calcula con las tareas de cuidado de hoy, y si las vacunas y desparasitaciones de {pet.nombre} están al día.
                          </div>,
                          document.body
                        )}

                      <p className="pet-card-v2-footer">{nextEvent}</p>
                    </div>
                    );
                  });
                })()}

                <div className="pet-card-v2-actions-stack">
                  <button
                    type="button"
                    className="pet-card-v2-mini pet-card-v2-mini-add"
                    onClick={handleAddPetClick}
                  >
                    <span className="pet-card-v2-mini-icon" aria-hidden>+</span>
                    <span className="pet-card-v2-mini-label">Añadir mascota</span>
                  </button>

                  <button
                    type="button"
                    className="pet-card-v2-mini pet-card-v2-mini-remove"
                    onClick={handleRemovePetClick}
                    disabled={pets.length === 0}
                    title={pets.length === 0 ? "No tienes mascotas para eliminar" : "Eliminar mascota"}
                  >
                    <span className="pet-card-v2-mini-icon" aria-hidden>−</span>
                    <span className="pet-card-v2-mini-label">Eliminar mascota</span>
                  </button>
                </div>
              </div>
            </div>

            {/* COLUMNA DERECHA: calendario + cuidados de hoy (SIEMPRE ACTIVO) */}
            <div className="calendar-section-row" id="calendar-section" ref={calendarRef}>
              <div className="calendar-col">
                <PetfamCalendar
                  idUser={userId}
                  onDateSelect={handleDateSelect}
                  birthdayEvents={birthdayEvents}
                  pets={pets}
                  refreshKey={calendarRefreshKey}
                  onOpenDrPaws={handleOpenDrPaws}
                  planActive={true}
                />
              </div>

              <div className="daily-care-card" ref={dailyCareRef}>
                <div className="daily-care-head">
                  <h3 className="daily-care-title">Cuidados de hoy</h3>
                  <span className="daily-care-points-badge">
                    <Trophy size={14} strokeWidth={2.2} />
                    +{dailyCarePoints} pts
                  </span>
                </div>
                <p className="daily-care-subtitle">Marca cada uno y suma puntos.</p>

                <div className="daily-care-progress">
                  <div className="daily-care-progress-row">
                    <span>PROGRESO</span>
                    <span>{dailyCareDoneCount}/{dailyCareTotal}</span>
                  </div>
                  <div className="daily-care-progress-bar">
                    <div className="daily-care-progress-fill" style={{ width: `${dailyCareProgressPercent}%` }} />
                  </div>
                </div>

                {dailyCareLoading && dailyCareTasks.length === 0 ? (
                  <p className="daily-care-subtitle" style={{ marginBottom: 20 }}>
                    Cargando cuidados de hoy…
                  </p>
                ) : dailyCareTasks.length === 0 ? (
                  <p className="daily-care-subtitle" style={{ marginBottom: 20 }}>
                    Añade una mascota para ver sus cuidados diarios aquí.
                  </p>
                ) : (
                  <div className="daily-care-list">
                    {dailyCareTasks.map((task) => (
                      <button
                        type="button"
                        key={task.id}
                        className={`daily-care-item ${task.done ? "is-done" : ""}`}
                        onClick={() => toggleDailyCareTask(task.id)}
                      >
                        <span className="daily-care-check" aria-hidden="true">
                          {task.done && <Check size={14} strokeWidth={3} />}
                        </span>
                        <span className="daily-care-item-text">
                          <span className="daily-care-item-title">{task.title}</span>
                          <span className="daily-care-item-meta">
                            {String(task.petName || "").toUpperCase()} · {(CATEGORY_LABELS[task.category] || task.category || "").toUpperCase()}
                          </span>
                        </span>
                        <span className="daily-care-item-points">+{task.points}</span>
                      </button>
                    ))}
                  </div>
                )}

                <div className="daily-care-tip">
                  <span className="daily-care-tip-label">TIP DEL DÍA</span>
                  <p className="daily-care-tip-text">{todayReminder}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CONTENIDO DE INTERÉS */}
        <section className="content-section" id="contenido" ref={reminderRef}>
          <div className="content-inner">
            <div className="content-header-row">
              <div className="content-heading">
                <span className="content-eyebrow">Contenido de interés</span>
                <h2 className="content-title">Aprende algo nuevo hoy</h2>
                <p className="content-subtitle">
                  Guías cortas y videos creados con veterinarios y etólogos. Guarda los que quieras leer o ver luego.
                </p>
              </div>

              <div className="content-filter-pills" role="tablist" aria-label="Filtrar contenido">
                {CONTENT_CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    role="tab"
                    aria-selected={contentFilter === cat}
                    className={`filter-pill ${contentFilter === cat ? "is-active" : ""}`}
                    onClick={() => setContentFilter(cat)}
                  >
                    <span>{cat}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="content-grid">
              {filteredArticles.map((article) => {
                const isSaved = savedContentIds.includes(article.id);
                const saveCount = contentSaveCounts[article.id] ?? 0;

                return (
                <article key={article.id} className="content-card">
                  <div className={`content-card-image content-card-image--${article.categorySlug}`}>
                    <div className="content-card-image-zoom" aria-hidden="true" />
                    <div className="content-card-overlay" aria-hidden="true" />

                    <span className="content-card-badge">{article.category.toUpperCase()}</span>
                    <button
                      type="button"
                      className={`content-card-bookmark ${isSaved ? "is-saved" : ""}`}
                      aria-label={isSaved ? "Quitar de guardados" : "Guardar guía"}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleContentSave(article.id);
                      }}
                    >
                      <Bookmark size={16} strokeWidth={2.2} fill={isSaved ? "currentColor" : "none"} />
                    </button>

                    <span className="content-card-read-now">
                      <span className="content-card-play" aria-hidden="true">
                        <Play size={13} fill="currentColor" strokeWidth={0} />
                      </span>
                      Leer ahora
                    </span>
                  </div>

                  <div className="content-card-body">
                    <h3 className="content-card-title">{article.title}</h3>
                    <p className="content-card-desc">{article.description}</p>

                    <div className="content-card-footer">
                      <span className="content-card-meta">
                        <Clock size={14} strokeWidth={2} />
                        {article.readTime} min de lectura
                      </span>
                      <span className="content-card-saves">
                        {saveCount.toLocaleString("es-CO")} guardados
                      </span>
                    </div>
                  </div>
                </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* TICKER DESLIZANTE */}
        <div className="ticker-section" aria-hidden="true">
          <div className="ticker-track">
            {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, idx) => (
              <span className="ticker-item" key={`${item}-${idx}`}>
                <PawPrint size={13} strokeWidth={2.2} />
                {item.toUpperCase()}
              </span>
            ))}
          </div>
        </div>

        {/* FOOTER */}
        <footer className="site-footer">
          <div className="footer-inner">
            <img src={petfamLogo} className="footer-logo" alt="Logo Petfam" />
            <p className="footer-text">© 2026 Petfam</p>
          </div>
        </footer>
      </div>

      {/* ===== PANEL EDITA TU PERFIL ===== */}
      {openProfilePanel && (
        <div
          className="profile-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpenProfilePanel(false);
          }}
        >
          <div className="profile-panel" onClick={(e) => e.stopPropagation()}>
            <button className="profile-close" onClick={() => setOpenProfilePanel(false)} aria-label="Cerrar edición de perfil">
              <X size={18} />
            </button>

            <h2 className="profile-title">Edita tu perfil</h2>

            <div className="profile-top">
              <div className="profile-photo-edit">
                <input ref={profileFileRef} type="file" accept="image/*" onChange={handleProfilePhotoChange} className="sr-only-input" />

                <button
                  type="button"
                  className={`profile-photo-box ${profilePhoto ? "has-photo" : ""}`}
                  onClick={() => profileFileRef.current?.click()}
                >
                  {profilePhoto ? (
                    <img src={profilePhoto} alt="Foto de perfil" />
                  ) : (
                    <>
                      <span className="profile-photo-plus">+</span>
                      <span className="profile-photo-text">Editar foto</span>
                    </>
                  )}
                </button>
              </div>

              <div className="profile-fields">
                <label className="profile-label">
  Nombre
  <input
    type="text"
    value={profileName}
    onChange={(e) => {
      const v = (e.target.value || "").toUpperCase();
      setProfileName(v);
      setEditedName(v);
    }}
    autoCapitalize="characters"
    spellCheck={false}
  />
</label>

                <label className="profile-label">
                  Celular
                  <input
                    type="tel"
                    value={profilePhone}
                    onChange={(e) => {
                      setProfilePhone(e.target.value);
                      setEditedPhone(e.target.value);
                    }}
                  />
                </label>

                <label className="profile-label">
                  Ubicación
                  <input
                    type="text"
                    value={profileLocation}
                    onChange={(e) => {
                      const v = e.target.value;
                      setProfileLocation(v);
                      setEditedLocation(v);
                      setCountry(v);
                    }}
                  />
                </label>

                <div className="profile-save-box" style={{ marginTop: "10px" }}>
                  {saveSuccess && <p className="save-success">✔ Cambios guardados</p>}
                  {saveError && <p className="save-error">⚠ {saveError}</p>}

                  <button
                    type="button"
                    className="profile-save-btn"
                    onClick={async () => {
                      setSavingProfile(true);
                      setSaveError("");
                      setSaveSuccess(false);

                      try {
                        const raw = localStorage.getItem("petfamUser");
                        if (!raw) throw new Error("No se encontró usuario en localStorage");
                        const user = JSON.parse(raw);

                        const id = resolveUserId(user);
                        if (!id) throw new Error("No se encontró idUser");

                        const res = await authFetch(`/user/${id}`, {
                          method: "PUT",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            name: editedName,
                            phone: editedPhone,
                            country: editedLocation,
                          }),
                        });

                        const json = await res.json().catch(() => null);
                        if (!res.ok) throw new Error(json?.userMessage || "Error guardando en backend");

                        const updatedUser = {
                          ...user,
                          ...(json?.data || {}),
                          name: editedName,
                          phone: editedPhone,
                          country: editedLocation,
                          location: editedLocation,
                        };
                        localStorage.setItem("petfamUser", JSON.stringify(updatedUser));

                        setProfileName(editedName);
                        setProfilePhone(editedPhone);
                        setProfileLocation(editedLocation);

                        setSaveSuccess(true);
                        setTimeout(() => setSaveSuccess(false), 1500);
                      } catch {
                        setSaveError("No se pudieron guardar los cambios");
                      } finally {
                        setSavingProfile(false);
                      }
                    }}
                    disabled={savingProfile}
                  >
                    {savingProfile ? "Guardando..." : "Guardar cambios"}
                  </button>
                </div>
              </div>
            </div>

            <div className="profile-bottom">
              <button
                type="button"
                className="profile-link"
                onClick={() => {
                  resetPasswordForm();
                  setOpenProfilePanel(false);
                  setOpenPasswordModal(true);
                }}
              >
                <Lock size={16} />
                <span>Editar contraseña</span>
              </button>
              <button
  type="button"
  className="profile-link"
  onClick={() => {
    setPayError("");
    setPaySuccess(false);
    setOpenPaymentModal(true);
  }}
>
  <span style={{ fontSize: 16 }}>💳</span>
  <span>Actualizar información de pago</span>
</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL: CONTENIDO GUARDADO ===== */}
      {openSavedContentModal && (
        <div
          className="password-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpenSavedContentModal(false);
          }}
        >
          <div className="password-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="password-close"
              onClick={() => setOpenSavedContentModal(false)}
              aria-label="Cerrar contenido guardado"
            >
              <X size={18} />
            </button>

            <h3 className="password-title">Contenido guardado</h3>

            {savedArticles.length === 0 ? (
              <p className="password-helper">
                Aún no has guardado nada. Toca el marcador en cualquier tarjeta de "Contenido de interés" para guardarla aquí.
              </p>
            ) : (
              <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
                {savedArticles.map((article) => (
                  <button
                    key={article.id}
                    type="button"
                    onClick={() => goToSavedContent(article)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      padding: "12px 14px",
                      borderRadius: 14,
                      border: "1px solid rgba(0,0,0,0.06)",
                      background: "rgba(255,255,255,0.65)",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                      <strong style={{ lineHeight: 1.2, color: "#0B2F3E" }}>{article.title}</strong>
                      <span style={{ fontSize: 12, opacity: 0.7, color: "#0B2F3E" }}>
                        {article.category} · {article.readTime} min
                      </span>
                    </div>

                    <ArrowUpRight size={18} color="#0B2F3E" style={{ flexShrink: 0 }} />
                  </button>
                ))}
              </div>
            )}

            <div className="password-actions" style={{ marginTop: 14 }}>
              <button
                type="button"
                className="password-cancel"
                onClick={() => setOpenSavedContentModal(false)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL CAMBIAR CONTRASEÑA ===== */}
      {openPasswordModal && (
        <div className="password-modal-overlay">
          <div className="password-modal pro">
            <button
              type="button"
              className="password-close"
              onClick={() => {
                resetPasswordForm();
                setOpenPasswordModal(false);
              }}
              aria-label="Cerrar"
            >
              <X size={18} />
            </button>

            <div className="password-header">
              <div className="password-logo">
                <img src={petfamLogo} alt="Petfam" />
              </div>

              <h3>Cambiar contraseña</h3>
              <p>
                Primero valida tu contraseña actual.
                <br />
                Luego podrás definir una nueva.
              </p>
            </div>

            {passwordError && <p className="password-message password-error">{passwordError}</p>}
            {passwordSuccess && <p className="password-message password-success">{passwordSuccess}</p>}

            <form onSubmit={handlePasswordSubmit} className="password-form">
              <label>
  Contraseña actual

  <div className="pw-field">
    <input
      type={showCurrentPassword ? "text" : "password"}
      value={currentPassword}
      onChange={(e) => setCurrentPassword(e.target.value)}
      placeholder="••••••••"
      autoFocus
    />

    <button
      type="button"
      className="pw-toggle"
      onClick={() => setShowCurrentPassword((v) => !v)}
      aria-label={showCurrentPassword ? "Ocultar contraseña actual" : "Mostrar contraseña actual"}
      title={showCurrentPassword ? "Ocultar" : "Mostrar"}
    >
      {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  </div>
</label>

<label>
  Nueva contraseña

  <div className="pw-field">
    <input
      type={showNewPassword ? "text" : "password"}
      value={newPassword}
      onChange={(e) => setNewPassword(e.target.value)}
      placeholder="Mínimo 8 caracteres"
    />

    <button
      type="button"
      className="pw-toggle"
      onClick={() => setShowNewPassword((v) => !v)}
      aria-label={showNewPassword ? "Ocultar nueva contraseña" : "Mostrar nueva contraseña"}
      title={showNewPassword ? "Ocultar" : "Mostrar"}
    >
      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  </div>
</label>

<label>
  Confirmar nueva contraseña

  <div className="pw-field">
    <input
      type={showConfirmPassword ? "text" : "password"}
      value={confirmPassword}
      onChange={(e) => setConfirmPassword(e.target.value)}
      placeholder="Repite la nueva contraseña"
    />

    <button
      type="button"
      className="pw-toggle"
      onClick={() => setShowConfirmPassword((v) => !v)}
      aria-label={showConfirmPassword ? "Ocultar confirmación" : "Mostrar confirmación"}
      title={showConfirmPassword ? "Ocultar" : "Mostrar"}
    >
      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  </div>
</label>


              <div className="password-actions">
                <button
                  type="button"
                  className="password-cancel"
                  onClick={() => {
                    resetPasswordForm();
                    setOpenPasswordModal(false);
                  }}
                  disabled={passwordLoading}
                >
                  Cancelar
                </button>

                <button type="submit" className="password-submit" disabled={passwordLoading}>
                  {passwordLoading ? "Guardando..." : "Guardar contraseña"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== MODAL: ELEGIR MASCOTA A ELIMINAR ===== */}
      {openDeletePetModal && (
        <div
          className="password-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setOpenDeletePetModal(false);
              setOpenConfirmDelete(false);
              setPetToDelete(null);
              setDeleteError("");
            }
          }}
        >
          <div className="password-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="password-close"
              onClick={() => {
                setOpenDeletePetModal(false);
                setOpenConfirmDelete(false);
                setPetToDelete(null);
                setDeleteError("");
              }}
              aria-label="Cerrar eliminación de mascota"
            >
              <X size={18} />
            </button>

            <h3 className="password-title">¿A qué mascota quieres eliminar?</h3>

            {deleteError && <p className="password-message password-error">{deleteError}</p>}

            {pets.length === 0 ? (
              <p className="password-helper">Aún no tienes mascotas registradas.</p>
            ) : (
              <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
                {pets.map((pet) => {
                  const avatarSrc = pet.foto || getBreedImage(pet.tipo, pet.raza);

                  return (
                    <div
                      key={pet.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "10px 12px",
                        borderRadius: 14,
                        border: "1px solid rgba(0,0,0,0.06)",
                        background: "rgba(255,255,255,0.65)",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: "50%",
                            overflow: "hidden",
                            flex: "0 0 44px",
                            background: "#fff",
                            border: "1px solid rgba(0,0,0,0.08)",
                          }}
                        >
                          <img src={avatarSrc} alt={pet.nombre} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        </div>

                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <strong style={{ lineHeight: 1.1 }}>{pet.nombre}</strong>
                          <span style={{ fontSize: 12, opacity: 0.75 }}>
                            {pet.tipo} {pet.raza ? `· ${pet.raza}` : ""}
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => askConfirmDelete(pet)}
                        aria-label={`Eliminar a ${pet.nombre}`}
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: 12,
                          border: "1px solid rgba(0,0,0,0.08)",
                          background: "white",
                          display: "grid",
                          placeItems: "center",
                          cursor: "pointer",
                        }}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="password-actions" style={{ marginTop: 14 }}>
              <button
                type="button"
                className="password-cancel"
                onClick={() => {
                  setOpenDeletePetModal(false);
                  setOpenConfirmDelete(false);
                  setPetToDelete(null);
                  setDeleteError("");
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL: CONFIRMAR ELIMINACIÓN ===== */}
      {openConfirmDelete && petToDelete && (
        <div
          className="password-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget && !deleteLoading) setOpenConfirmDelete(false);
          }}
        >
          <div className="password-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="password-close"
              onClick={() => !deleteLoading && setOpenConfirmDelete(false)}
              aria-label="Cerrar confirmación"
            >
              <X size={18} />
            </button>

            <h3 className="password-title">Confirmar eliminación</h3>
            <p className="password-helper">
              ¿Estás seguro de eliminar a <strong>{petToDelete.nombre}</strong>?
            </p>

            {deleteError && <p className="password-message password-error">{deleteError}</p>}

            <div className="password-actions">
              <button type="button" className="password-cancel" onClick={() => setOpenConfirmDelete(false)} disabled={deleteLoading}>
                No
              </button>

              <button type="button" className="password-submit" onClick={doDeletePet} disabled={deleteLoading}>
                {deleteLoading ? "Eliminando..." : "Sí, eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL LÍMITE DE MASCOTAS ===== */}
      {showPetLimitModal && (
        <div className="password-modal-overlay">
          <div className="password-modal">
            <button type="button" className="password-close" onClick={() => setShowPetLimitModal(false)} aria-label="Cerrar aviso de límite de mascotas">
              <X size={18} />
            </button>

            <h3 className="password-title">Has alcanzado el límite de mascotas</h3>
            <p className="password-helper">
              Puedes tener hasta <strong>2 mascotas</strong> incluidas en tu plan.
            </p>
          </div>
        </div>
      )}

      {/* ✅ Modal: elegir mascota para Dr.Paws */}
      {openDrPawsPicker && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 16,
          }}
          onClick={() => setOpenDrPawsPicker(false)}
        >
          <div
            style={{
              width: "min(420px, 100%)",
              background: "#fff8f0",
              borderRadius: 18,
              padding: 16,
              boxShadow: "0 18px 40px rgba(0,0,0,.18)",
              border: "1px solid rgba(0,0,0,.06)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 style={{ margin: 0, color: "#0b2f3e" }}>¿Para qué mascota quieres hablar con la Dra.Vetia?</h4>
            <p style={{ marginTop: 6, marginBottom: 14, fontSize: 13, opacity: 0.75 }}>
              Selecciona una de tus mascotas registradas.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {pets?.map((p) => (
                <button
                  key={p.id ?? p.idPet ?? p.id_pet}
                  type="button"
                  onClick={() => handlePickPetForDrPaws(p)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    padding: "12px 14px",
                    borderRadius: 14,
                    border: "1px solid rgba(0,0,.08)",
                    background: "#ffffff",
                    cursor: "pointer",
                    fontWeight: 600,
                    color: "#0b2f3e",
                  }}
                >
                  <span>{p.nombre ?? p.name ?? "Mascota"}</span>
                  <span style={{ fontSize: 12, opacity: 0.7 }}>
                    {(p.tipo ?? p.species ?? "").toString().toLowerCase()}
                  </span>
                </button>
              ))}
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button
                type="button"
                onClick={() => setOpenDrPawsPicker(false)}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  borderRadius: 14,
                  border: "none",
                  background: "#f2deca",
                  cursor: "pointer",
                  fontWeight: 600,
                  color: "#0b2f3e",
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Chat Dr.Paws */}
      {openVetChat && chatPet && (
        <VetAssistantChat
          pet={chatPet}
          onClose={() => setOpenVetChat(false)}
          onVaccinesSaved={() => setCalendarRefreshKey((k) => k + 1)}
        />
      )}
      {/* ===== MODAL: ACTUALIZAR INFO DE PAGO ===== */}
{openPaymentModal && (
  <div
    className="pay-modal-overlay"
    onClick={(e) => {
      if (e.target === e.currentTarget && !payLoading) setOpenPaymentModal(false);
    }}
  >
    <div className="pay-modal" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="pay-close"
        onClick={() => !payLoading && setOpenPaymentModal(false)}
        aria-label="Cerrar"
      >
        <X size={18} />
      </button>

      <h3 className="pay-title">Actualizar información de pago</h3>

      <p className="pay-sub">
        <span className="pay-sub2">Por seguridad, no almacenamos el número de tu tarjeta.</span>
      </p>

      {/* ✅ selector tipo tarjeta */}
      <div className="pay-types" role="tablist" aria-label="Tipo de tarjeta">
        <button
          type="button"
          className={`pay-type-btn ${cardType === "visa" ? "active" : ""}`}
          onClick={() => setCardType("visa")}
          disabled={payLoading}
          aria-label="Visa"
        >
          <img src={visa} alt="Visa" loading="lazy" />
        </button>

        <button
          type="button"
          className={`pay-type-btn ${cardType === "mc" ? "active" : ""}`}
          onClick={() => setCardType("mc")}
          disabled={payLoading}
          aria-label="Mastercard"
        >
          <img src={mc} alt="Mastercard" loading="lazy" />
        </button>

        <button
          type="button"
          className={`pay-type-btn ${cardType === "amex" ? "active" : ""}`}
          onClick={() => setCardType("amex")}
          disabled={payLoading}
          aria-label="American Express"
        >
          <img src={amex} alt="American Express" loading="lazy" />
        </button>
      </div>

      {/* ✅ preview tarjeta */}
      <div className="pay-card-preview" aria-hidden="true">
        <div className="pay-card-top">
          <img src={chipImg} className="pay-chip-img" alt="" />
          <img
            src={cardType === "visa" ? visa : cardType === "mc" ? mc : amex}
            className="pay-type-logo"
            alt=""
          />
        </div>

        <div className="pay-card-num">
          {cardNumber ? formatCardNumber(cardNumber) : "0000 0000 0000 0000"}
        </div>

        <div className="pay-card-row">
          <div className="pay-card-name">{cardHolder ? cardHolder : "NOMBRE TITULAR"}</div>
          <div className="pay-card-exp">
            {(cardExpMonth || "MM")}/{(cardExpYear || "YY")}
          </div>
        </div>
      </div>

      {payError && <div className="pay-msg pay-error">⚠ {payError}</div>}
      {paySuccess && <div className="pay-msg pay-ok">✔ Fuente de pago actualizada</div>}

      <div className="pay-form">
        <label className="pay-label">
          Nombre y apellido del titular
          <input
            className="pay-input"
            value={cardHolder}
            onChange={(e) => setCardHolder(normalizeCardHolder(e.target.value))}
            placeholder="Tu nombre"
            autoFocus
          />
        </label>

        <label className="pay-label">
          Número de tarjeta
          <input
            className="pay-input"
            value={cardNumber}
            onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
            inputMode="numeric"
            placeholder="4242 4242 4242 4242"
            maxLength={19}
          />
        </label>

        <div className="pay-grid">
          <label className="pay-label">
            Mes
            <input
              className="pay-input"
              value={cardExpMonth}
              onChange={(e) => setCardExpMonth(onlyDigits(e.target.value).slice(0, 2))}
              inputMode="numeric"
              placeholder="MM"
              maxLength={2}
            />
          </label>

          <label className="pay-label">
            Año
            <input
              className="pay-input"
              value={cardExpYear}
              onChange={(e) => setCardExpYear(onlyDigits(e.target.value).slice(0, 2))}
              inputMode="numeric"
              placeholder="YY"
              maxLength={2}
            />
          </label>

          <label className="pay-label">
            CVC
            <input
              className="pay-input"
              value={cardCvc}
              onChange={(e) => setCardCvc(onlyDigits(e.target.value).slice(0, 4))}
              inputMode="numeric"
              placeholder="CVV"
              maxLength={4}
            />
          </label>
        </div>

        <div className="pay-actions">
          <button
            type="button"
            className="pay-cancel"
            onClick={() => !payLoading && setOpenPaymentModal(false)}
            disabled={payLoading}
          >
            Cancelar
          </button>

          <button
            type="button"
            className="pay-save"
            onClick={handleSavePaymentSource}
            disabled={payLoading}
          >
            {payLoading ? "Guardando..." : "Guardar tarjeta"}
          </button>
        </div>
      </div>
    </div>
  </div>
)}
{openVideoGuide && (
  <div
    className="video-guide-overlay"
    onClick={(e) => {
      if (e.target === e.currentTarget) setOpenVideoGuide(false);
    }}
  >
    <div className="video-guide-modal" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="video-guide-close"
        onClick={() => setOpenVideoGuide(false)}
        aria-label="Cerrar video guiado"
      >
        <X size={18} />
      </button>

      <h3 className="video-guide-title">Video guiado</h3>

      <p className="video-guide-subtitle">
        {isMobileVideo
          ? "Conoce cómo moverte en la plataforma."
          : "Conoce cómo moverte en la plataforma."}
      </p>

      <div className="video-guide-box">
  <video
    key={isMobileVideo ? "cel" : "pc"}
    controls
    autoPlay
    playsInline
    preload="metadata"
    className="video-guide-player"
  >
    <source
      src={isMobileVideo ? "/videos/videocel.mp4" : "/videos/videopc.mp4"}
      type="video/mp4"
    />
    Tu navegador no soporta video.
  </video>
</div>
    </div>
  </div>
)}

      {/* ✅ MODAL CARGANDO MEDISMART (Cita de etología) */}
      {showMedismart &&
        createPortal(
          <div className="ms-overlay">
            <div className="ms-loader-box">
              <div className="ms-spinner"></div>
              <p>Estamos preparando tu consulta con Medismart.life...</p>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}